import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  useDailyBankBalances,
  useSchedulePayments,
  useStatementPayments,
  useCreateStatementPayment,
  useUpdateStatementPayment,
  useStatementPlans,
  useSchedulePaymentReconciliation,
  useConfirmScheduleClearances,
  useCreateSchedulePaymentException,
  useUpdateSchedulePaymentException,
  useBulkSchedulePaymentExceptions,
  useUpdateSchedulePayment,
  useDeleteSchedulePayment,
  useForecastsBatch,
  useReportsCollectionSummary,
  useMonthlyCompensations,
  useSearchNetsuiteChecks,
  useSearchNetsuiteChecksBySubsidiary,
  useBankAccounts,
  useTransfers,
  useCreateTransfer,
  useUpdateTransfer,
  useClinics,
  useNetsuiteBankBalances,
} from "@api/hooks";
import { Loading, showSuccess, showApiError, usePersistedState, SegmentedControl } from "@common";
import { useAuth } from "@api/useAuth";
import api from "@api/api";
import endpoints from "@api/endpoints";
import { buildUrlWithParams } from "@api/queryHelpers";
import {
  calculateDdsDraw,
  torontoToday,
  canManageSchedulePayments,
  canDeleteSchedulePayments,
  canManageTransfers,
  isOrganizer,
} from "@utils";
import { parseMonthDayFromDateString } from "@utils/checkDateUtils";
import { downloadCsv } from "@utils/downloadCsv";
import {
  filterAssociateFeeEligibleRows,
  buildChecksByVendor,
  computeExpectedClearInfoByVendor,
  matchChecksToRows,
} from "@utils/checkMatching";
import useClinicFilterOptions from "@hooks/useClinicFilterOptions";
import ClinicCashFlowCard, {
  getDayWindow,
  shiftISO,
  computeBalanceSeries,
} from "./ClinicCashFlowCard";
import { ClinicFilterBar } from "@common";
import CashFlowSummary from "./CashFlowSummary";
import TransferModal from "./actions/TransferModal";
import MoveTransferDateModal from "./actions/MoveTransferDateModal";
import {
  CARD_PAYMENT_KEY,
  CARD_PAYMENT_LABEL,
  buildReconciliationIndex,
  buildScheduleOutflows,
  scheduledAmountsByDate,
  takeScheduleMatch,
  checkRowFor,
  sortOutflowRows,
  splitWeekdays,
} from "./scheduleOccurrences";
import { buildCardStatementDetail } from "./cardStatementDetail";
import MoveOccurrenceModal from "./MoveOccurrenceModal";
import SchedulePaymentModal from "./SchedulePaymentModal";
import StatementPaymentModal from "./schedule_payment/StatementPaymentModal";
import { planToFormValues } from "./schedule_payment/statementPlan";
import useNonBankingMovePrompt from "./useNonBankingMovePrompt";
import ConfirmClearedModal from "./ConfirmClearedModal";
import ConfirmNonBankingMovesModal from "./ConfirmNonBankingMovesModal";

// ─── constants ────────────────────────────────────────────────────────────────

// NetSuite bank account ID for the SFC corporate account (RBC S.F. Corporation − 9411).
// Used to detect SFC as a transfer endpoint (from/to_account_id) since its
// clinic code is not a "real" clinic in the sortedClinics list.
const SFC_BANK_ACCOUNT_ID = 1009;

// How far back the uncleared-check search looks. A check still outstanding
// after this long is stale data, not a real claim on the balance, and searching
// without a bound makes the query walk the account's whole history.
const CHECK_LOOKBACK_MONTHS = 12;

// ─── helpers ──────────────────────────────────────────────────────────────────

// Associate fee checks for a given cash-flow month are payroll for the
// *previous* production month (checks are cut the month after they're earned).
function getPreviousMonth(yearMonth) {
  if (!yearMonth) return null;
  const [year, month] = yearMonth.split("-").map(Number);
  const d = new Date(year, month - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// NetSuite date → "YYYY-MM-DD", the key every cash-flow day column uses. The
// subsidiary check endpoint TO_CHARs its dates, but NetSuite elsewhere hands
// back the account's display format, so "3/14/2026" is accepted too rather than
// silently producing a key that matches no column.
function toISODate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    return `${iso[1]}-${String(iso[2]).padStart(2, "0")}-${String(iso[3]).padStart(2, "0")}`;
  }
  const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) {
    return `${us[3]}-${String(us[1]).padStart(2, "0")}-${String(us[2]).padStart(2, "0")}`;
  }
  return null;
}

// The 1st of the month `monthsBack` months before today, as "YYYY-MM-DD".
// Every check search that isn't scoped to a single month uses this as its
// lower bound. Anchored to the 1st so the generated SQL text only changes
// monthly and every user shares one warm SuiteQL cache entry, instead of
// taking a fresh miss per day (or per window step).
function monthStartBefore(monthsBack) {
  const [y, m] = torontoToday().split("-").map(Number);
  const startY = m > monthsBack ? y : y - 1;
  const startM = ((m - monthsBack - 1 + 12) % 12) + 1;
  return `${startY}-${String(startM).padStart(2, "0")}-01`;
}

// Associate-fee check memos are written by admin/Check.jsx as
//   "Associate Fee - April 2026 - NL-G-EXP - Smiles Analytics"
// so the clinic code is one of the " - " segments. Reading it back is the only
// way to attribute an outstanding check from an older payroll month, which has
// no comp row in the current window to carry a clinic. Returns the code in its
// canonical casing, or null when no segment is a known clinic.
function parseClinicFromMemo(memo, codeByUpper) {
  if (!memo) return null;
  for (const part of String(memo).split(" - ")) {
    const hit = codeByUpper[part.trim().toUpperCase()];
    if (hit) return hit;
  }
  return null;
}

// Per-(clinic, date) accumulator for the daily bank balance entries. One clinic
// can hold several accounts, so every figure below is summed across them before
// being used. Shared by realBalanceByClinic and the SFC-only equivalent, which
// parse identical payloads — SFC is fetched separately only because it is an
// inactive pseudo-clinic and never appears in the selected-clinic list.
function emptyBalanceSlot() {
  return {
    balance: 0,
    locBal: 0,
    valid: false,
    closing: 0,
    closingTime: null,
    closingValid: false,
    closingLoc: 0,
    closingLocTime: null,
    closingLocValid: false,
  };
}

// Fold one reference-only closing blob ({balance, time}) into a slot, under the
// `closing` / `closingLoc` key prefix. These are structurally unlike the numeric
// figures beside them: schemaless JSON, and never part of the projection. The
// kept time is the LATEST across the clinic's accounts, since that is when the
// day's picture was actually complete. A malformed or empty blob is ignored
// rather than counted as zero, so the row shows "—" instead of a false $0.
function addClosingBlob(slot, blob, key) {
  if (!blob || typeof blob !== "object") return;
  const amount = parseFloat(blob.balance);
  if (Number.isNaN(amount)) return;
  slot[key] += amount;
  slot[`${key}Valid`] = true;
  const timeKey = `${key}Time`;
  if (blob.time && (!slot[timeKey] || blob.time > slot[timeKey])) slot[timeKey] = blob.time;
}

// Fold one daily_bank_balance entry into its day's slot.
function addEntryToSlot(slot, entry) {
  const bal = parseFloat(entry.balance);
  if (!Number.isNaN(bal)) {
    slot.balance += bal;
    slot.valid = true;
  }
  const locBal =
    entry.loc_balance != null && entry.loc_balance !== "" ? parseFloat(entry.loc_balance) : 0;
  if (!Number.isNaN(locBal)) slot.locBal += locBal;

  addClosingBlob(slot, entry.closing_balance, "closing");
  addClosingBlob(slot, entry.closing_loc_balance, "closingLoc");
}

// date → { amount, time }. Reference data: no seed, no pre-roll, no carry-forward
// to the next day — a day either has a real closing figure or shows nothing.
// Built without the `slot.valid` guard the projection uses, so a day carrying a
// closing balance but no usable opening balance still reports it.
// Closing Available, the reference-only mirror of the Available Balance row:
//     closing balance + (LOC limit − closing LOC balance)
// Keyed off the closing BALANCE: with no closing figure there is nothing to
// compute from, and emitting the bare LOC headroom would read as a real
// position. A missing closing LOC counts as zero drawn, matching how the entry
// page treats a blank LOC. The stamp is the later of the two, since that is when
// the pair was complete.
function closingAvailableFrom(closingByDay, closingLocByDay, locLimit = 0) {
  const out = {};
  Object.entries(closingByDay).forEach(([d, closing]) => {
    const loc = closingLocByDay[d];
    out[d] = {
      amount: closing.amount + (locLimit - (loc?.amount ?? 0)),
      time: loc?.time && (!closing.time || loc.time > closing.time) ? loc.time : closing.time,
    };
  });
  return out;
}

// Merge one clinic's reference map into a running By Company total: amounts sum
// across clinics, and the stamp becomes the latest of them — the point at which
// every clinic that reported had reported.
function mergeClosingInto(target, source) {
  Object.entries(source ?? {}).forEach(([date, ref]) => {
    const prev = target[date];
    if (!prev) {
      target[date] = { amount: ref.amount, time: ref.time ?? null };
      return;
    }
    prev.amount += ref.amount;
    if (ref.time && (!prev.time || ref.time > prev.time)) prev.time = ref.time;
  });
}

// Chip text for a credit card's "Statement balance" row: when the balance is
// owed, e.g. "due Sep 20". Rendered beside the row label by `DueDatePill` in
// ClinicCashFlowCard, which is also where the reasoning for a chip over a
// column lives.
//
// Dates are parsed as local midnight (`T00:00:00`), not as bare ISO, which JS
// reads as UTC and would render as the previous day west of Greenwich.
//
// Several statements can be outstanding inside one window (August's, due in
// September, alongside September's), so every due date in view is named — the
// cells sum those statements, and a single date would misdescribe the total.
// Breakdown maps ({ rowKey: { date: [lines] } }) combined per row and day, so
// sources sharing a row (scheduled rent + an unnumbered rent check) both show.
function mergeBreakdowns(...sources) {
  const out = {};
  sources.forEach((source) => {
    Object.entries(source ?? {}).forEach(([rowKey, byDate]) => {
      const row = (out[rowKey] = { ...(out[rowKey] ?? {}) });
      Object.entries(byDate ?? {}).forEach(([day, lines]) => {
        row[day] = [...(row[day] ?? []), ...(lines ?? [])];
      });
    });
  });
  return out;
}

function statementDueLabel(dueDates) {
  if (!dueDates || dueDates.size === 0) return null;
  const fmt = (iso) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString("en-CA", { month: "short", day: "numeric" });
  return `due ${[...dueDates].sort().map(fmt).join(", ")}`;
}

function closingByDayFrom(byDay, key = "closing") {
  const out = {};
  Object.entries(byDay).forEach(([d, slot]) => {
    if (slot[`${key}Valid`]) out[d] = { amount: slot[key], time: slot[`${key}Time`] };
  });
  return out;
}

function Chevron({ open }) {
  return (
    <svg
      className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
      viewBox="0 0 10 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M1 1l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── page ─────────────────────────────────────────────────────────────────────

/**
 * How old the bank data is, and a click to go and get it again.
 *
 * The reconciliation is cached for six hours on the server and shared across
 * everyone, so the figures on this page can have been computed before the user
 * arrived — and a scheduled payment that cleared an hour ago will not show as
 * cleared until that entry expires. Nothing on the page said so, which makes a
 * stale answer indistinguishable from a current one.
 *
 * The age comes from the response's `computed_at` (when the SuiteQL scan ran),
 * not from when this client received it: those differ by up to the whole TTL,
 * and the scan time is the one that describes the data.
 *
 * Clicking clears and re-pulls rather than merely recomputing. Three caches sit
 * between this button and NetSuite — react-query's, the reconciliation result's
 * (6h), and the SuiteQL rows' (1h) — and getting past only the first two would
 * re-run the matching over the same hour-old rows, so a payment that cleared
 * ten minutes ago still would not appear however many times a person clicked.
 *
 * Deliberately a button rather than an automatic refetch on an interval. Each
 * refresh is two paged SuiteQL scans against an API that is slow and
 * concurrency-limited for every user of it at once, so it happens when someone
 * actually wants it to.
 */
function CacheFreshness({ query, onRefresh }) {
  const computedAt = query.data?.computed_at;

  // Re-render once a minute so "3m ago" does not sit there saying "just now"
  // for the rest of the session. Only while there is something to age.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!computedAt) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 60 * 1000);
    return () => clearInterval(id);
  }, [computedAt]);

  const age = useMemo(() => {
    if (!computedAt) return null;
    const then = new Date(computedAt);
    if (Number.isNaN(then.getTime())) return null;
    const minutes = Math.max(0, Math.round((Date.now() - then.getTime()) / 60000));
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}m ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }, [computedAt]);

  const refreshing = query.isFetching;
  // Past an hour the data is old enough that a reader should be nudged rather
  // than merely informed, so the control stops being quiet grey.
  const stale = !!computedAt && Date.now() - new Date(computedAt).getTime() > 60 * 60 * 1000;

  return (
    <button
      type="button"
      onClick={onRefresh}
      disabled={refreshing}
      title={
        computedAt
          ? `Bank matching computed ${new Date(computedAt).toLocaleString()}.\n` +
            "Click to clear the cache and pull fresh rows from NetSuite — two scans, a few seconds."
          : "Click to pull bank data from NetSuite now."
      }
      // h-9 to sit level with the pickers beside it — the bar aligns on
      // `items-end` and every control in it is that height.
      className={`flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors disabled:cursor-wait ${
        refreshing
          ? "border-palette-sky-border bg-palette-sky-soft text-palette-sky-text"
          : stale
            ? "border-palette-amber-border bg-palette-amber-soft text-palette-amber-text hover:bg-palette-amber-muted"
            : "border-palette-gray-divider bg-white text-palette-gray-secondary hover:bg-palette-gray-subtle"
      }`}
    >
      <RefreshIcon spinning={refreshing} />
      {/* "Reconciled", not "Bank data": the age describes when the MATCHING
          last ran, which is the thing that goes stale — the balances beside it
          come from elsewhere and are not what this button refreshes. Avoiding
          "synced"/"updated" too, since both imply this writes something, and
          the whole reconciliation is read-only. */}
      {refreshing ? "Reconciling…" : age ? `Reconciled ${age}` : "Reconcile now"}
    </button>
  );
}

function RefreshIcon({ spinning }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={`h-3.5 w-3.5 ${spinning ? "animate-spin" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <path d="M14 8a6 6 0 1 1-1.76-4.24" strokeLinecap="round" />
      <path d="M14 2v4h-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Reconciliation status, stated plainly at the top of the page.
 *
 * The per-cell markers only appear where there is something to mark, so with
 * nothing matched the grid looks identical to a grid where reconciliation never
 * ran — a silent failure and a genuinely quiet month are indistinguishable.
 * This says which it is, and it is also where the heuristic gets declared:
 * matching is on amount + date proximity, and a reader deserves to know that
 * before trusting a green tick.
 */
function ReconciliationStatus({ query }) {
  const counts = query.data?.counts;

  const base =
    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap";

  if (query.isError) {
    return (
      <span
        className={`${base} bg-palette-rose-soft text-palette-rose-text`}
        title="The cash-flow grid is fully usable without this — scheduled payments simply show on their own dates, unmarked."
      >
        <span className="h-1.5 w-1.5 rounded-full bg-palette-rose-badge" />
        Bank match unavailable
      </span>
    );
  }
  // `isFetching` rather than `isLoading`: the hook keeps the previous window's
  // answer as placeholder data, so a month change refetches with data already
  // in hand and `isLoading` would stay false while the numbers are still stale.
  if (query.isFetching && !counts) {
    return (
      <span className={`${base} bg-palette-sky-soft text-palette-sky-text`}>
        <span className="h-1.5 w-1.5 rounded-full bg-palette-sky-badge animate-pulse" />
        Matching payments to bank…
      </span>
    );
  }
  if (!counts) return null;

  // A window entirely in the future has nothing to reconcile — saying "0
  // cleared" there would imply a failed match rather than a window that has not
  // happened yet.
  if (!counts.matched_against) {
    return (
      <span
        className={`${base} bg-palette-gray-subtle text-palette-gray-secondary`}
        title="Nothing in this window is due yet, so there is nothing to match against the bank."
      >
        <span className="h-1.5 w-1.5 rounded-full bg-palette-gray-placeholder" />
        Nothing due yet to reconcile
      </span>
    );
  }

  return (
    <span
      className={`${base} ${
        counts.rolled_forward > 0
          ? "bg-palette-amber-soft text-palette-amber-text"
          : "bg-palette-emerald-soft text-palette-emerald-text"
      }`}
      // Short by design: a native tooltip cannot be scrolled and a long one
      // gets clipped, so the detail lives in the grid (per-payment chips) and
      // this only has to say what the numbers mean.
      title={
        `Matched on amount (within $${query.data?.params?.tolerance}) and date ` +
        `(within ${query.data?.params?.window_days} days). Hover a cell for each payment's evidence.` +
        (counts.not_yet_due ? ` ${counts.not_yet_due} not due yet are not matched.` : "")
      }
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          counts.rolled_forward > 0 ? "bg-palette-amber-badge" : "bg-palette-emerald-badge"
        }`}
      />
      {counts.cleared} cleared
      {counts.rolled_forward > 0 && ` · ${counts.rolled_forward} overdue carried forward`}
      {counts.with_suggestions > 0 && ` · ${counts.with_suggestions} possible`}
      {counts.not_yet_due > 0 && ` · ${counts.not_yet_due} not due yet`}
      {query.isFetching && " · refreshing…"}
    </span>
  );
}

// The Cash Flow projection grid. This component is ONLY the "Cash Flow" tab —
// the tab bar and the sibling tabs (Schedule Payment, Daily Bank Balance,
// Actions) live in AccessClinicCashFlow.
export default function ClinicCashFlow({
  canMoveOccurrence: canMoveProp,
  canFundTransfer: canFundProp,
}) {
  const { user } = useAuth();

  // The grid is mostly read-only, but two cell interactions write: clicking an
  // outflow cell moves a scheduled-payment occurrence, and clicking an expected
  // associate-fee cell creates/moves a funding transfer. They hit different
  // backend resources with different write sets, so they gate separately —
  // Bookkeeper reaches this tab but may do neither.
  const canMoveOccurrence = canMoveProp ?? canManageSchedulePayments(user);
  const canFundTransfer = canFundProp ?? canManageTransfers(user);

  // Forcing a fresh reconciliation is Organizer-only. Everyone still SEES the
  // reconciled grid — the figures are cached and shared — but only an organizer
  // can spend two NetSuite scans to rebuild them.
  //
  // Enforced HERE ONLY: the endpoint still honours `?refresh=` from anyone who
  // may read it, so this is a UI decision about who is offered the control, not
  // a permission. Nothing is exposed by it — the data is the same either way —
  // but a caller crafting the query string by hand is not stopped.
  const canReconcile = isOrganizer(user);

  // Rolling window, not a calendar month: the grid always shows `spanDays` days
  // starting at `rangeStart`, which defaults to TODAY. ◀ / ▶ step a whole window
  // so the ranges tile without gaps. Stored as an ISO date rather than an offset
  // so switching 30 ⇄ 60 days keeps the same start instead of jumping.
  const [rangeStart, setRangeStart] = usePersistedState("cashFlow:rangeStart", () =>
    torontoToday(),
  );
  const [areaManagerFilter, setAreaManagerFilter] = usePersistedState(
    "cashFlow:areaManagerFilter",
    "",
  );
  const [clinicFilters, setClinicFilters] = usePersistedState("cashFlow:clinicFilters", []);
  const [spanDays, setSpanDays] = usePersistedState("cashFlow:spanDays", 30);
  const [viewMode, setViewMode] = usePersistedState("cashFlow:viewMode", "daily");
  // "clinic" → one table per clinic (default); "company" → a single table
  // totalling every selected clinic.
  const [groupMode, setGroupMode] = usePersistedState("cashFlow:groupMode", "clinic");
  const [cardOpen, setCardOpen] = useState({});
  const [companyOpen, setCompanyOpen] = useState(true);
  const [sfcOpen, setSfcOpen] = useState(true);

  const { clinicsQuery, areaManagerOptions, clinicOptions } =
    useClinicFilterOptions(areaManagerFilter);

  // Still needed for `clinicIdByCode` below. include_inactive covers SFC, whose
  // clinic row is inactive while its bank account is not.
  const allClinicsQuery = useClinics({ include_inactive: true });

  // Clinic code → numeric PK, so the balance queries can filter by `clinic_id`
  // instead of code: an indexed integer FK compare rather than a string compare
  // on the joined `clinics` row, and no code-casing to get wrong.
  // Sourced from the same include_inactive fetch above, so SFC is covered.
  const clinicIdByCode = useMemo(() => {
    const map = {};
    (allClinicsQuery.data?.results ?? []).forEach((c) => {
      if (c.clinic && c.id != null) map[c.clinic] = c.id;
    });
    return map;
  }, [allClinicsQuery.data]);

  // An empty clinic filter means NOTHING is selected, not "everything". Every
  // query on this page fans out per clinic — checks, balances, compensations —
  // so defaulting to all 30+ clinics made simply opening the page the most
  // expensive thing it does. The user picks first; the grid renders after.
  //
  // Choosing an area manager is still a choice, though, and it CLEARS the
  // clinic picker (see onAreaManagerChange below) — so it resolves to that
  // manager's clinics, which is what `clinicOptions` already narrows to.
  const activeClinics = useMemo(() => {
    if (clinicFilters.length > 0) return clinicFilters;
    return areaManagerFilter ? clinicOptions.map((o) => o.value) : [];
  }, [clinicFilters, clinicOptions, areaManagerFilter]);

  const sortedClinics = useMemo(() => {
    const optMap = Object.fromEntries(clinicOptions.map((o) => [o.value, o.label]));
    return [...activeClinics].sort((a, b) => {
      const nameA = (optMap[a] ?? a)
        .replace(/\s*\(.*\)$/, "")
        .trim()
        .toLowerCase();
      const nameB = (optMap[b] ?? b)
        .replace(/\s*\(.*\)$/, "")
        .trim()
        .toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [activeClinics, clinicOptions]);

  useEffect(() => {
    setCardOpen((prev) => {
      const next = {};
      activeClinics.forEach((c) => {
        next[c] = prev[c] ?? true;
      });
      return next;
    });
  }, [activeClinics]);

  const days = useMemo(() => getDayWindow(rangeStart, spanDays), [rangeStart, spanDays]);
  const from = days.length ? days[0].date : undefined;
  const to = days.length ? days[days.length - 1].date : undefined;

  // Calendar month CONTAINING the window start. The window itself is no longer
  // month-aligned, but a few sources are still month-keyed — associate-fee
  // placement and the payroll-month queries below — so they anchor here.
  const month = from ? from.slice(0, 7) : null;

  // Pre-roll: the `spanDays` days immediately BEFORE the visible window. The
  // opening balance of the first visible day is the *projected* ending of the
  // day before it — obtained by rolling the last real balance forward through
  // the pre-roll cashflow (see projectedSeedByClinic), so day 1's opening is a
  // continuation rather than a stale real balance from weeks earlier.
  const preRollStart = useMemo(() => (from ? shiftISO(from, -spanDays) : null), [from, spanDays]);
  const preRollDays = useMemo(
    () => (preRollStart ? getDayWindow(preRollStart, spanDays) : []),
    [preRollStart, spanDays],
  );

  // Payroll month for the associate-fee queries: checks are cut the month AFTER
  // the production month they pay for.
  const prevMonth = useMemo(() => getPreviousMonth(month), [month]);

  // Opening-balance source: our own Daily Bank Balance entries (replaces the
  // old NetSuite banking-statement feed). Fetch every entry up to the end of
  // the visible month for the visible clinics, then carry the latest known
  // balance forward across days (see balanceByClinicDay).
  // Filtered by clinic_id, not code — see `clinicIdByCode`. Gated on the id map
  // being loaded, otherwise an empty id list would silently fetch every clinic.
  const selectedClinicIds = useMemo(
    () => sortedClinics.map((code) => clinicIdByCode[code]).filter((id) => id != null),
    [sortedClinics, clinicIdByCode],
  );
  const dailyBalancesQuery = useDailyBankBalances(
    { clinic_id: selectedClinicIds.join(","), date_to: to },
    { enabled: !!month && selectedClinicIds.length > 0 },
  );
  // SFC daily balance fetched separately (SFC is inactive so never in sortedClinics).
  const sfcClinicId = clinicIdByCode.SFC;
  const sfcDailyBalancesQuery = useDailyBankBalances(
    { clinic_id: sfcClinicId, date_to: to },
    // Also gated on a clinic being picked: with an empty filter the page
    // renders nothing, so SFC's balance has nowhere to show.
    { enabled: !!month && sfcClinicId != null && sortedClinics.length > 0 },
  );

  // Bank accounts come from the local registry, and the display name is the
  // stored `label` — no NetSuite round trip. Labels were resolved once by
  // `manage.py backfill_bank_account_labels`, so they read e.g.
  // "Bank Balance : RBC - Arden Park Dental -[8117]".
  //
  // One query covers every use on this page — card headers, the transfer picker,
  // and the per-clinic credit card rows — including SFC, since the registry
  // isn't filtered by the clinic selection.
  //
  // Declared HERE rather than beside its other consumers further down: the
  // balance parsing below needs to know which accounts are credit cards to keep
  // their balances out of the cash position, and a `const` read before its
  // declaration is a TDZ error, not a hoist.
  const bankAccountsQuery = useBankAccounts({ status: "active", limit: 500 });

  // Adapted to the field names the transfer modal and the fee-funding handler
  // already expect (`bank_account_id`/`bank_name`/`clinic`), so nothing
  // downstream had to change.
  const bankAccounts = useMemo(
    () =>
      (bankAccountsQuery.data?.results ?? []).map((a) => ({
        // Local BankAccount PK — what a transfer is WRITTEN with.
        id: a.id,
        // NetSuite account id — what the JE is POSTED with.
        bank_account_id: a.netsuite_account_id,
        bank_name: a.label,
        clinic: a.clinic_code,
        clinic_name: a.clinic_name || a.entity_name,
        // `bank` = cash held; `credit_card` / `line_of_credit` = money owed.
        // Everything below that treats an account as this clinic's cash account
        // filters on this.
        account_type: a.account_type,
        credit_limit:
          a.credit_limit != null && a.credit_limit !== "" ? Number(a.credit_limit) : null,
        // This account's OWN line of credit. Null means it carries none — the
        // normal case for a clinic's second account, since the facility sits
        // with exactly one of them.
        locLimit: a.loc_limit != null && a.loc_limit !== "" ? Number(a.loc_limit) : null,
        // Card-only, and used by the statement panel alone: the last digits
        // that tell two of a clinic's cards apart. Null on a bank account.
        account_number: a.account_number || null,
        // The issuing bank's own name. Distinct from `bank_name` above, which
        // this mapping has always set to the account LABEL for the transfer
        // modal's benefit — so the real one needs its own key.
        card_bank_name: a.bank_name || null,
      })),
    [bankAccountsQuery.data],
  );

  // Cash accounts only. The maps that answer "which account IS this clinic"
  // (the header label, the NetSuite GL row) are last-wins across a clinic's
  // accounts, so a Visa the clinic also holds would otherwise win them.
  const cashAccounts = useMemo(
    () => bankAccounts.filter((a) => a.account_type !== "credit_card"),
    [bankAccounts],
  );

  // LOC limit per clinic — used to turn a raw entered bank balance into the
  // "available" balance the Daily Bank Balance page shows:
  //   available = balance + (LOC limit − LOC balance)
  //
  // Sourced from the ACCOUNTS, not the clinic profile: since migration
  // bank_accounts.0005 the limit is a column on `BankAccount`, because a clinic
  // can hold several accounts and only one of them carries the facility. Summed
  // per clinic and then, as before, applied exactly ONCE per clinic per date in
  // `availableFor` — never per account, which would multiply the headroom by the
  // account count.
  //
  // Only one account per clinic carries a limit today, so the sum is that one
  // value; it is a sum rather than a lookup so a clinic that genuinely holds two
  // facilities adds up instead of silently reporting one of them.
  const locLimitByClinic = useMemo(() => {
    const map = {};
    cashAccounts.forEach((a) => {
      if (!a.clinic || a.locLimit == null) return;
      map[a.clinic] = (map[a.clinic] ?? 0) + a.locLimit;
    });
    return map;
  }, [cashAccounts]);

  // clinic code → its active credit cards, one entry per card. Clinic-owned
  // only: entity-level cards (Dens N Dente's Visas) are the entity cash flow
  // page's business and have no clinic to group under here.
  const cardAccountsByClinic = useMemo(() => {
    const map = {};
    bankAccounts.forEach((a) => {
      if (a.account_type !== "credit_card" || !a.clinic) return;
      (map[a.clinic] = map[a.clinic] || []).push({
        id: a.id,
        // Row keys must collide with neither a ROW_DEFS key nor another card's,
        // so they are namespaced by the account's PK.
        key: `card${a.id}`,
        label: a.bank_name,
        creditLimit: a.credit_limit,
        // Identity + terms for the statement panel below the grid. The rows in
        // the grid itself need none of this.
        bankName: a.card_bank_name,
        accountNumber: a.account_number,
        clinicCode: a.clinic,
      });
    });
    Object.values(map).forEach((cards) =>
      cards.sort((x, y) => String(x.label).localeCompare(String(y.label))),
    );
    return map;
  }, [bankAccounts]);

  // Account ids that are credit cards — the guard the cash aggregation uses. A
  // card balance is money OWED, so summing it into a clinic's available balance
  // would both overstate the cash on hand and roll that error forward through
  // every projected day after it.
  const cardAccountIds = useMemo(() => {
    const ids = new Set();
    bankAccounts.forEach((a) => {
      if (a.account_type === "credit_card") ids.add(a.id);
    });
    return ids;
  }, [bankAccounts]);

  // clinicCode → { realByDay: {dayOfMonth → real balance}, seed }. `realByDay`
  // holds only days with an actual entry in the visible month; `seed` is the
  // most recent real balance from *before* the month, used to anchor the
  // opening projection until an in-month entry appears. The card
  // (computeBalanceSeries) turns this into the per-day opening/ending series:
  // real value where entered, otherwise carried from the prior day's ending.
  const realBalanceByClinic = useMemo(() => {
    if (!month || !from || !to) return {};
    const entries = dailyBalancesQuery.data?.results ?? dailyBalancesQuery.data ?? [];

    // The anchor value for the projection is the *available* balance, matching
    // the Daily Bank Balance page: available = balance + (LOC limit − LOC balance).
    //
    // `balance`/`locBal` arrive already SUMMED across every account the clinic
    // holds on that date (see the per-date aggregation below). The LOC limit is
    // added exactly once: `locLimitByClinic` has already totalled the clinic's
    // accounts, so applying it per account here would multiply the headroom by
    // the account count.
    const availableFor = (code, balance, locBal) => {
      if (Number.isNaN(balance)) return NaN;
      const locLimit = locLimitByClinic[code] ?? 0;
      return balance + (locLimit - locBal);
    };

    // Balances are keyed per BANK ACCOUNT now, so one clinic can have several
    // rows for the same date (one per account). Collapse them to a single figure
    // per (clinic, date) by summing.
    //
    // Entries with no `clinic_code` are entity-level accounts (e.g. Dens N Dente)
    // — they have no place on this clinic-grouped grid, so they're skipped.
    //
    // Caveat: this sums raw amounts, which is only meaningful within one
    // currency. Every clinic-owned account is CAD today; a USD clinic account
    // would need converting before it could be added here, and there is no FX
    // source in the app.
    const byClinic = {};
    entries.forEach((e) => {
      const code = e.clinic_code;
      if (!code) return;
      // Credit card accounts are excluded: their `balance` is money OWED, not
      // cash held, so it must not be summed into the clinic's position. They
      // get their own reference rows instead (see `cardBalancesByClinic`).
      if (cardAccountIds.has(e.bank_account)) return;
      const day = e.date.slice(0, 10);
      const perClinic = (byClinic[code] = byClinic[code] || {});
      const slot = (perClinic[day] = perClinic[day] || emptyBalanceSlot());
      addEntryToSlot(slot, e);
    });

    const result = {};
    Object.entries(byClinic).forEach(([code, byDay]) => {
      // Keyed by ISO date so it lines up with the day columns, which can span
      // more than one month.
      //   realByDay        — real entries inside the visible window [from, to]
      //   preRollRealByDay — real entries inside the pre-roll month [preRollStart, from)
      //   seed             — most recent real balance BEFORE the pre-roll month,
      //                      the anchor the pre-roll projection starts from
      const realByDay = {};
      const preRollRealByDay = {};
      const closingByDay = closingByDayFrom(byDay);
      const closingLocByDay = closingByDayFrom(byDay, "closingLoc");
      const closingAvailableByDay = closingAvailableFrom(
        closingByDay,
        closingLocByDay,
        locLimitByClinic[code] ?? 0,
      );
      let seed = null;
      let seedDate = null;
      Object.entries(byDay).forEach(([d, slot]) => {
        if (!slot.valid) return;
        const available = availableFor(code, slot.balance, slot.locBal);
        if (d >= from && d <= to) {
          realByDay[d] = available;
        } else if (preRollStart && d >= preRollStart && d < from) {
          preRollRealByDay[d] = available;
        } else if ((preRollStart ? d < preRollStart : d < from) && (!seedDate || d > seedDate)) {
          seedDate = d;
          seed = available;
        }
      });
      result[code] = {
        realByDay,
        preRollRealByDay,
        seed,
        closingByDay,
        closingLocByDay,
        closingAvailableByDay,
      };
    });
    return result;
  }, [dailyBalancesQuery.data, month, from, to, preRollStart, locLimitByClinic, cardAccountIds]);

  // SFC real balance — same parsing logic as realBalanceByClinic but for SFC only.
  const sfcRealBalance = useMemo(() => {
    if (!month || !from || !to)
      return {
        realByDay: {},
        preRollRealByDay: {},
        seed: null,
        closingByDay: {},
        closingLocByDay: {},
        closingAvailableByDay: {},
      };
    const entries = sfcDailyBalancesQuery.data?.results ?? sfcDailyBalancesQuery.data ?? [];
    const sfcLocLimit = locLimitByClinic["SFC"] ?? 0;
    const realByDay = {};
    const preRollRealByDay = {};
    let seed = null;
    let seedDate = null;

    // Same per-account fan-out as realBalanceByClinic: SFC may hold more than
    // one account, so sum per date before applying the (single) LOC limit.
    const byDay = {};
    entries.forEach((e) => {
      // Same exclusion as realBalanceByClinic — a card balance is a liability.
      if (cardAccountIds.has(e.bank_account)) return;
      const d = e.date.slice(0, 10);
      const slot = (byDay[d] = byDay[d] || emptyBalanceSlot());
      addEntryToSlot(slot, e);
    });

    Object.entries(byDay).forEach(([d, slot]) => {
      if (!slot.valid) return;
      const available = slot.balance + (sfcLocLimit - slot.locBal);
      if (d >= from && d <= to) {
        realByDay[d] = available;
      } else if (preRollStart && d >= preRollStart && d < from) {
        preRollRealByDay[d] = available;
      } else if ((preRollStart ? d < preRollStart : d < from) && (!seedDate || d > seedDate)) {
        seedDate = d;
        seed = available;
      }
    });
    const closingByDay = closingByDayFrom(byDay);
    const closingLocByDay = closingByDayFrom(byDay, "closingLoc");
    return {
      realByDay,
      preRollRealByDay,
      seed,
      closingByDay,
      closingLocByDay,
      closingAvailableByDay: closingAvailableFrom(closingByDay, closingLocByDay, sfcLocLimit),
    };
  }, [sfcDailyBalancesQuery.data, month, from, to, preRollStart, locLimitByClinic, cardAccountIds]);

  // clinic code → { [cardKey]: { balance: {date → {amount, time}}, available } }
  // for every clinic-owned credit card, read off the SAME daily_bank_balance
  // rows the cash position comes from (they were skipped above as liabilities).
  //
  // Point-in-time reference figures, exactly like the closing rows: no seed, no
  // pre-roll, no carry-forward, so a day with no entry shows "—" rather than a
  // stale balance presented as today's.
  //
  // `balance` prefers the reference CLOSING figure and falls back to the entered
  // `balance` column (the "Credit Balance" on the entry page) — the closing
  // snapshot is the end-of-day number a statement reconciles against, where
  // `balance` is whatever was owed when the row happened to be typed.
  //
  // `available` is the card's headroom against that same figure:
  //     credit limit − balance owed
  // matching the entry page's Visa-mode Available exactly. A card with no limit
  // on file gets no Available figure rather than a headroom of "−owed".
  const cardBalancesByClinic = useMemo(() => {
    const out = {};
    // id → { clinic, key, creditLimit }, so an entry finds its card in one
    // lookup instead of scanning every clinic's list per row.
    const cardById = new Map();
    Object.entries(cardAccountsByClinic).forEach(([clinic, cards]) => {
      cards.forEach((c) => cardById.set(c.id, { ...c, clinic }));
    });
    if (cardById.size === 0) return out;

    const entries = dailyBalancesQuery.data?.results ?? dailyBalancesQuery.data ?? [];
    entries.forEach((e) => {
      const card = cardById.get(e.bank_account);
      if (!card) return;

      // Closing blob first. It is schemaless JSON, so a missing or garbled
      // value falls through to the entered balance rather than reading as 0.
      let amount = NaN;
      let time = null;
      const closing = e.closing_balance;
      if (closing && typeof closing === "object") {
        const parsed = parseFloat(closing.balance);
        if (!Number.isNaN(parsed)) {
          amount = parsed;
          time = closing.time ?? null;
        }
      }
      if (Number.isNaN(amount)) amount = parseFloat(e.balance);
      if (Number.isNaN(amount)) return;

      const date = e.date.slice(0, 10);
      const perClinic = (out[card.clinic] = out[card.clinic] || {});
      const slot = (perClinic[card.key] = perClinic[card.key] || { balance: {}, available: {} });
      slot.balance[date] = { amount, time };
      if (card.creditLimit != null) {
        slot.available[date] = { amount: card.creditLimit - amount, time };
      }
    });
    return out;
  }, [dailyBalancesQuery.data, cardAccountsByClinic]);

  // Declared here rather than beside its other consumer below: the
  // reconciliation query underneath is now the first thing to need it, and a
  // `const` read before its declaration is a TDZ error, not a hoist.
  const clinicFilterParam = useMemo(() => sortedClinics.join(","), [sortedClinics]);

  // Credit-card statements for the clinics on screen — the Monthly Inputs tab's
  // rows, read back onto the grid.
  //
  // Scoped by the CARD's clinic (`?clinic=` takes the codes this page already
  // holds), not by the visible accounts: a statement touches two accounts, and
  // the paying bank could belong to a clinic that is on screen while the card's
  // does not. Deliberately NOT bounded by date — `date_from`/`date_to` bound
  // `statement_date`, but a statement's PAYMENTS can fall a month or two later
  // (`due_month_offset` reaches 2), so a window filter would drop exactly the
  // statements whose splits land in view. The splits are filtered by date below
  // instead. Same call shape as EntityCashFlowTable's, so the two grids cannot
  // disagree about which statements exist.
  const statementsQuery = useStatementPayments(
    { status: "active", clinic: clinicFilterParam, limit: 500 },
    { enabled: sortedClinics.length > 0 },
  );

  // A statement moves money BOTH ways, and this grid shows the two sides in
  // different places:
  //   the FROM bank account -> the paying CLINIC's "Credit card payment" outflow
  //   the TO card account   -> that card's own "Statement balance" row
  // so one memo derives both from the same rows and they cannot drift.
  //
  // Split dates are what place an outflow — each split carries its own date, so
  // a three-way split lands in three columns rather than all on the due date.
  // The statement BALANCE is a position, not a flow: it is shown on every day
  // the statement is outstanding (issued .. due, inclusive), which is the span
  // over which "what does this card owe" has that answer.
  const statementFlowsByClinic = useMemo(() => {
    const out = {};
    if (!from || !to) return out;
    const rows = statementsQuery.data?.results ?? statementsQuery.data ?? [];
    if (!rows.length) return out;

    // Paying side: account id -> clinic code. Cards are in here too, but a
    // statement's `from` account is a bank by validation, so it cannot match one.
    const clinicByAccount = new Map();
    bankAccounts.forEach((a) => {
      if (a.clinic) clinicByAccount.set(a.id, a.clinic);
    });
    // Receiving side: card account id -> { clinic, row key }, so a statement
    // finds the block it belongs to in one lookup.
    const cardById = new Map();
    Object.entries(cardAccountsByClinic).forEach(([clinic, cards]) => {
      cards.forEach((c) => cardById.set(c.id, { clinic, key: c.key }));
    });

    const bucket = (code) =>
      (out[code] = out[code] ?? {
        // {date -> amount} — the outflow child row, and its contribution to the
        // Outflows total.
        cardPayment: {},
        // The same, over the pre-roll month: it never renders, it only rolls the
        // seed balance into the window (see projectedSeedByClinic).
        preRollCardPayment: {},
        // {date -> [{label, amount}]} for the cell's hover panel, keyed like the
        // other breakdowns this page passes down.
        breakdown: {},
        // cardKey -> { balanceByDay: {date -> {amount}}, dueDates: Set }
        byCard: {},
      });

    rows.forEach((st) => {
      const cardLabel = st.to_card_account_label ?? "Credit card";

      // ── the paying bank's outflow ──────────────────────────────────────────
      const payerCode = clinicByAccount.get(st.from_bank_account);
      if (payerCode) {
        const acct = bucket(payerCode);
        (Array.isArray(st.splits) ? st.splits : []).forEach((split) => {
          const date = split?.date;
          const amount = Number(split?.amount);
          if (!date || !Number.isFinite(amount)) return;
          if (date >= from && date <= to) {
            acct.cardPayment[date] = (acct.cardPayment[date] ?? 0) + amount;
            (acct.breakdown[date] = acct.breakdown[date] ?? []).push({
              label: cardLabel,
              amount,
              // What makes the cell editable. The row has no SchedulePayment
              // behind it, so the Move-occurrence modal cannot apply; carrying
              // the statement's own id lets a click open the STATEMENT instead,
              // through the same `openCardStatementEditor` the panel below the
              // grid already uses.
              statementId: st.id,
              cardAccountId: st.to_card_account,
            });
          } else if (preRollStart && date >= preRollStart && date < from) {
            acct.preRollCardPayment[date] = (acct.preRollCardPayment[date] ?? 0) + amount;
          }
        });
      }

      // ── the card's own statement balance ──────────────────────────────────
      const card = cardById.get(st.to_card_account);
      const balance = Number(st.balance);
      if (!card || !Number.isFinite(balance) || !st.statement_date || !st.due_date) return;
      // The statement's own payments, in date order — what pays this balance
      // DOWN. Read from every split, not just the visible ones: a payment made
      // before the window opened has already reduced what is owed on the first
      // day the grid draws, and ignoring it would show the full balance again.
      const paidSplits = (Array.isArray(st.splits) ? st.splits : [])
        .map((sp) => ({ date: sp?.date, amount: Number(sp?.amount) }))
        .filter((sp) => sp.date && Number.isFinite(sp.amount))
        .sort((x, y) => x.date.localeCompare(y.date));
      const lastSplit = paidSplits.length ? paidSplits[paidSplits.length - 1].date : null;
      // A plan can run past the due date, and money still owed on the 25th does
      // not stop being owed because the 24th has gone by — so the row runs to
      // whichever comes last. Days after the due date stop once the balance is
      // cleared (below): a settled statement should not trail a row of zeros.
      const spanEnd = lastSplit && lastSplit > st.due_date ? lastSplit : st.due_date;
      // Only the part of that span the window actually shows, so a statement
      // crossing a month boundary appears on both months' grids without either
      // one iterating days it will never render.
      const start = st.statement_date > from ? st.statement_date : from;
      const end = spanEnd < to ? spanEnd : to;
      if (start > end) return;
      const acct = bucket(card.clinic);
      const slot = (acct.byCard[card.key] = acct.byCard[card.key] ?? {
        balanceByDay: {},
        dueDates: new Set(),
      });
      slot.dueDates.add(st.due_date);
      // Payments already made BEFORE the first visible day, applied once up
      // front rather than re-summed per day.
      let paid = 0;
      let next = 0;
      while (next < paidSplits.length && paidSplits[next].date < start) {
        paid += paidSplits[next].amount;
        next += 1;
      }
      for (let d = start; d <= end; d = shiftISO(d, 1)) {
        // A payment reduces the balance ON the day it is made, so it is applied
        // before the cell is written: a $1,000 statement paid $250 on Oct 9
        // reads 1,000 on the 8th and 750 on the 9th.
        // How much this particular day pays off, kept apart from the running
        // total so the cell can say a payment LANDED here — the balance falling
        // is the consequence, not the event.
        let paidToday = 0;
        while (next < paidSplits.length && paidSplits[next].date <= d) {
          paidToday += paidSplits[next].amount;
          paid += paidSplits[next].amount;
          next += 1;
        }
        // Clamped: an overpaid statement owes nothing, not a negative amount.
        const remaining = Math.max(balance - paid, 0);
        // Summed: two statements outstanding on one day owe their total. No
        // `time` — a statement balance is a term of the statement, not a
        // moment something was observed at, so there is no capture stamp to show.
        const prevCell = slot.balanceByDay[d];
        slot.balanceByDay[d] = {
          amount: (prevCell?.amount ?? 0) + remaining,
          time: null,
          // Summed like the balance: two statements can both take a payment on
          // one day, and the cell is marking the day, not one of them.
          paidToday: (prevCell?.paidToday ?? 0) + paidToday,
        };
        // The last payment's day is the last day this statement has anything to
        // say. It is WRITTEN first — that cell is the one showing the balance
        // reaching zero — and the row ends after it, whether the statement
        // cleared early or ran past its due date. Days beyond it are empty
        // because there is no longer a statement outstanding, which is a
        // different thing from owing nothing.
        if (remaining === 0) break;
      }
    });

    // A day whose statements all sit at zero is a SETTLED day, not a day that
    // happens to owe $0 — and that is worth saying in the cell rather than
    // leaving a bare figure to be read as an amount. Done as a pass at the end
    // because it is a question about the day's TOTAL: one statement clearing
    // while another is still outstanding is not a settled day.
    Object.values(out).forEach((acct) => {
      Object.values(acct.byCard).forEach((slot) => {
        Object.values(slot.balanceByDay).forEach((cell) => {
          if (cell.amount === 0) cell.note = "paid";
        });
      });
    });
    return out;
  }, [statementsQuery.data, bankAccounts, cardAccountsByClinic, from, to, preRollStart]);

  // Scheduled payments overlaid on their specific dates in the visible month.
  // NO status filter. A cancelled schedule keeps the occurrences it produced
  // BEFORE its `end_date` — that money left the bank, and dropping the row here
  // would erase paid history from the grid the moment someone stopped a series.
  // Cancellation is bounded by the date, not by omitting the row.
  const schedulePaymentsQuery = useSchedulePayments({ limit: 1000 });

  // clinic code → { cardKey → statement panel model }. One panel per clinic-owned
  // card, rendered under that card's rows (see CardStatementPanel).
  //
  // Built from rows this page already has: the card's statements, and the
  // schedules whose paying ACCOUNT is the card — those are charges ON the card,
  // which is why they are pulled straight from the schedule rows rather than
  // from the per-clinic outflow grouping, where they would read as clinic cash
  // leaving the bank. Nothing here touches the projection.
  const cardDetailsByClinic = useMemo(() => {
    if (!from || !to) return {};
    const statementRows = statementsQuery.data?.results ?? statementsQuery.data ?? [];
    const scheduleRows = schedulePaymentsQuery.data?.results ?? schedulePaymentsQuery.data ?? [];
    const today = torontoToday();
    const out = {};
    Object.entries(cardAccountsByClinic).forEach(([code, cards]) => {
      const perCard = {};
      cards.forEach((card) => {
        const detail = buildCardStatementDetail({
          card,
          statements: statementRows.filter((st) => st.to_card_account === card.id),
          schedules: scheduleRows,
          // The card's own entered balances — what "card use" is measured
          // against. Same series the block's Balance row draws, so the panel
          // and the row cannot disagree about what is drawn on the card.
          balanceByDay: cardBalancesByClinic[code]?.[card.key]?.balance ?? {},
          today,
          windowFrom: from,
          windowTo: to,
        });
        if (detail) perCard[card.key] = detail;
      });
      out[code] = perCard;
    });
    return out;
  }, [
    statementsQuery.data,
    schedulePaymentsQuery.data,
    cardAccountsByClinic,
    cardBalancesByClinic,
    from,
    to,
  ]);

  // Which of those occurrences actually cleared the bank. The backend matches
  // each one against NetSuite's ledger and bank feed on amount + date
  // proximity — the two sides share no key — and returns `cleared` per
  // occurrence. Read-only: a weekly schedule is ONE row covering many
  // occurrences, so this cannot be stored and is recomputed per window.
  //
  // The grid does not wait on it. Until it resolves, payments render on their
  // own dates exactly as before; when it lands, the cleared ones are marked and
  // the overdue ones move onto today.
  // `lookbackDays` is load-bearing on the DEFAULT view. The window starts at
  // today, so it contains no past — without a lookback there would be no
  // overdue payment to carry onto today, and the feature would appear to do
  // nothing.
  //
  // 45, not 90: the lookback is the single biggest cost in the whole request,
  // because it widens the NetSuite scan far more than the visible window does.
  // Measured across all 36 accounts, dropping the scan from ~105 days to ~45
  // took the ledger side from 31s to 8s. A payment more than six weeks overdue
  // is a conversation, not a cash-flow line, so the tail buys little.
  const RECONCILE_LOOKBACK_DAYS = 45;
  // Bumped by the freshness button. Not persisted: a refresh is a thing someone
  // asked for once, and restoring it on the next visit would silently re-run two
  // NetSuite scans on every page load.
  const [reconcileNonce, setReconcileNonce] = useState(0);
  const reconciliationQuery = useSchedulePaymentReconciliation(
    {
      clinic: clinicFilterParam,
      from,
      to,
      lookbackDays: RECONCILE_LOOKBACK_DAYS,
      refreshNonce: reconcileNonce,
    },
    { enabled: !!clinicFilterParam && !!from && !!to },
  );
  const reconciliationIndex = useMemo(
    () => buildReconciliationIndex(reconciliationQuery.data),
    [reconciliationQuery.data],
  );

  // Everything DUE that nobody has ruled on yet — not only the matched ones.
  //
  // A payment with no NetSuite row and no bank row is exactly the case a person
  // has to settle by hand: the money may well have gone out and simply not been
  // booked, or been booked somewhere the matcher cannot see. Offering only the
  // matches would leave those permanently unresolvable — showing as overdue for
  // ever, with no way to say otherwise. So the list is every past occurrence
  // without a decision, and the modal shows plainly which have evidence.
  //
  // Future occurrences are excluded: nothing dated after today can have
  // cleared, the same rule the matcher follows.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingReview = useMemo(() => {
    const data = reconciliationQuery.data;
    if (!data?.items) return [];
    const today = data.today;
    return data.items
      .filter((i) => !i.decision && i.date <= today)
      .slice()
      .sort(
        (a, b) =>
          // Matched first — those are the quick agree-and-move-on — then oldest
          // first within each group, since the oldest unexplained payment is the
          // one most worth looking at.
          (b.match ? 1 : 0) - (a.match ? 1 : 0) || String(a.date).localeCompare(String(b.date)),
      );
  }, [reconciliationQuery.data]);
  const pendingMatchCount = pendingReview.filter((i) => i.match).length;
  const confirmClearances = useConfirmScheduleClearances({
    onSuccess: (rows) => {
      showSuccess(rows.length === 1 ? "Payment updated." : `${rows.length} payments updated.`);
    },
    // showApiError takes the error alone — it derives the message from the
    // response, which for this endpoint is the validation detail naming the
    // offending item. More useful than a generic string, and the write is
    // all-or-nothing so nothing was partially applied.
    onError: (err) => showApiError(err),
  });

  const {
    valuesByClinic,
    extraRowsByClinic: scheduleExtraRowsByClinic,
    occurrencesByClinic,
  } = useMemo(() => {
    if (!month || !from || !to)
      return { valuesByClinic: {}, extraRowsByClinic: {}, occurrencesByClinic: {} };
    const rows = schedulePaymentsQuery.data?.results ?? schedulePaymentsQuery.data ?? [];
    // `today` is what an uncleared past payment rolls onto. Toronto's today,
    // not UTC's — the same day the grid marks as current, or a payment would
    // roll onto a column that is not the one highlighted.
    return buildScheduleOutflows(rows, from, to, {
      // Only once the call has actually returned. Handing over an empty index
      // mid-flight would read as "nothing cleared" and stack the whole month's
      // past payments onto today, then unstack them a moment later.
      reconciliation: reconciliationQuery.data ? reconciliationIndex : null,
      today: torontoToday(),
      // Must match what the reconcile call asked for, or the grid would
      // generate occurrences the API said nothing about — and an occurrence the
      // API did not mention is treated as unknown, so it would silently never
      // roll.
      lookbackDays: RECONCILE_LOOKBACK_DAYS,
    });
  }, [schedulePaymentsQuery.data, month, from, to, reconciliationIndex, reconciliationQuery.data]);

  // An occurrence the rule put on a weekend or a bank holiday cannot clear that
  // day. Moving it to the next banking day is a stored exception — a real change
  // to when money leaves — so it is proposed in a modal rather than applied.
  // Gated on the same permission as a manual move: a read-only viewer sees the
  // grid, it is not asked to rewrite it.
  const nonBankingMoves = useNonBankingMovePrompt(occurrencesByClinic, {
    enabled: canMoveOccurrence,
  });

  // Associate Fee outflow: real per-practitioner compensation + NetSuite check
  // data for the *previous* production month (checks clear the month after
  // they're earned). Cleared checks land on their real clear date (actual);
  // everything else lands on the vendor's historical expected-clear day
  // (see checkMatching.js — shared with admin/Check.jsx so the matching rules
  // never drift between the two pages).
  const compensationsQuery = useMonthlyCompensations(
    { month: prevMonth, ...(clinicFilterParam ? { clinic: clinicFilterParam } : {}) },
    { enabled: !!prevMonth && sortedClinics.length > 0 },
  );
  const compRows = useMemo(
    () =>
      filterAssociateFeeEligibleRows(
        compensationsQuery.data?.results ?? compensationsQuery.data ?? [],
      ),
    [compensationsQuery.data],
  );

  const compVendorIds = useMemo(() => {
    const seen = new Set();
    compRows.forEach((r) => {
      const vid = r.netsuite_vendor_id;
      if (vid !== null && vid !== undefined && vid !== "") seen.add(String(vid));
    });
    return Array.from(seen);
  }, [compRows]);

  // ONE request for both jobs below, deliberately NOT month-scoped:
  //   · this payroll month's checks, to match against the comp rows
  //   · every still-outstanding check from ANY month, which a month-scoped
  //     search cannot see — a check cut three months ago and never cleared is
  //     still money owed
  // The month-scoped response is a strict SUBSET of this one (the backend's
  // `month` param only narrows trandate), so asking for both would mean two
  // SuiteQL round trips for data one already contains. The subset is recovered
  // in JS by `feeCheckTrandateRange` instead — same filter, no second query.
  //
  // Bounded by CHECK_LOOKBACK_MONTHS rather than left open: `month` is what
  // normally puts an indexed trandate predicate on this query, and without one
  // the unindexable `UPPER(memo) LIKE '%...%'` has to scan every check these
  // vendors ever received. `date_from` restores that predicate while still
  // reaching back over the months an outstanding check could be sitting in.
  const feeChecksFrom = useMemo(() => monthStartBefore(CHECK_LOOKBACK_MONTHS), []);
  const feeChecksQuery = useSearchNetsuiteChecks(
    { entities: compVendorIds, memo: "Associate Fee", dateFrom: feeChecksFrom },
    { enabled: compVendorIds.length > 0 },
  );
  const feeCheckItems = useMemo(() => {
    const items =
      feeChecksQuery.data?.items ?? feeChecksQuery.data?.results ?? feeChecksQuery.data ?? [];
    return Array.isArray(items) ? items : [];
  }, [feeChecksQuery.data]);

  // The half-open [start, end) trandate window the backend's `month` param
  // would have applied — checks for payroll month M are cut in M+1, which is
  // exactly the displayed `month`. Mirrors _trandate_range_after_month().
  const feeCheckTrandateRange = useMemo(() => {
    if (!month) return null;
    const [y, m] = month.split("-").map(Number);
    if (!y || !m) return null;
    const endY = m === 12 ? y + 1 : y;
    const endM = m === 12 ? 1 : m + 1;
    return [`${month}-01`, `${endY}-${String(endM).padStart(2, "0")}-01`];
  }, [month]);

  // Checks matched against the comp rows stay scoped to this payroll month.
  // matchChecksToRows pairs by AMOUNT, so widening the pool to every month
  // would let an old check of the same value match this month's row.
  const checksByVendor = useMemo(() => {
    if (!feeCheckTrandateRange) return buildChecksByVendor([]);
    const [start, end] = feeCheckTrandateRange;
    return buildChecksByVendor(
      feeCheckItems.filter((c) => {
        const d = toISODate(c?.trandate);
        return d && d >= start && d < end;
      }),
    );
  }, [feeCheckItems, feeCheckTrandateRange]);
  // Historical clear-day per vendor, rebuilt from the checks already fetched
  // rather than a third round trip to /checks/cleared-dates/. That endpoint
  // returns each vendor's 5 most recent cleared checks across ALL memos; this
  // is the same 5-most-recent shape restricted to ASSOCIATE FEE checks, which
  // predicts when the next associate fee check clears at least as well — and
  // costs no extra SuiteQL query.
  const clearedDatesData = useMemo(() => {
    const datesByVendor = {};
    feeCheckItems.forEach((c) => {
      const isCleared = c?.cleared === true || c?.cleared === "T" || c?.cleared === "t";
      const date = toISODate(c?.cleareddate);
      const vendorId = c?.entity_id;
      if (!isCleared || !date || vendorId === null || vendorId === undefined) return;
      const key = String(vendorId);
      (datesByVendor[key] = datesByVendor[key] || []).push(date);
    });

    const items = {};
    Object.entries(datesByVendor).forEach(([vendorId, dates]) => {
      dates.sort().reverse(); // newest first, matching the endpoint's ORDER BY
      items[vendorId] = {
        netsuite_vendor_id: Number(vendorId),
        items: dates.slice(0, 5).map((cleareddate) => ({ cleareddate })),
      };
    });
    return { items };
  }, [feeCheckItems]);

  const expectedClearInfoByVendor = useMemo(
    () => computeExpectedClearInfoByVendor(clearedDatesData, parseMonthDayFromDateString),
    [clearedDatesData],
  );
  const rowChecksMap = useMemo(
    () => matchChecksToRows(compRows, checksByVendor, { getExpectedAmount: calculateDdsDraw }),
    [compRows, checksByVendor],
  );

  // Still outstanding, any month. Cleared ones are dropped: those are either
  // already on the row via the matched pass, or settled history.
  const outstandingFeeChecks = useMemo(
    () =>
      feeCheckItems.filter(
        (c) => !(c?.cleared === true || c?.cleared === "T" || c?.cleared === "t"),
      ),
    [feeCheckItems],
  );

  // Memo lookups for the outstanding pass: clinic code by its uppercase form,
  // and practitioner name by NetSuite vendor id (checks carry neither).
  const clinicCodeByUpper = useMemo(() => {
    const map = {};
    sortedClinics.forEach((code) => {
      map[String(code).toUpperCase()] = code;
    });
    return map;
  }, [sortedClinics]);

  const vendorNameById = useMemo(() => {
    const map = {};
    compRows.forEach((r) => {
      const vid = r.netsuite_vendor_id;
      if (vid !== null && vid !== undefined && vid !== "" && r.practitioner_name) {
        map[String(vid)] = r.practitioner_name;
      }
    });
    return map;
  }, [compRows]);

  // Place each practitioner's amount on the current (`month`) calendar, best
  // evidence first:
  //   cleared check   → its real cleared date, the check's amount. Settled, so
  //                     it deducts from the balance and is never moved.
  //   uncleared check → the check's own amount and trandate. Real money the
  //                     bank hasn't taken yet.
  //   no check yet    → the projected draw on the vendor's historical average
  //                     clear day.
  // Both uncleared cases are "expected": shown for reference, never deducted,
  // and pulled forward to today once their date has passed. Same-day amounts
  // across practitioners (and clinics) are summed into one Associate Fee cell.
  const associateFeeByClinic = useMemo(() => {
    const valuesByClinic = {};
    const breakdownsByClinic = {};
    if (!month) return { valuesByClinic, breakdownsByClinic };
    const [year, mon] = month.split("-").map(Number);
    const daysInMonth = new Date(year, mon, 0).getDate();
    const todayStr = torontoToday();
    const todayInWindow = !!from && !!to && todayStr >= from && todayStr <= to;

    // Every uncleared check consumed by a comp row below, so the outstanding
    // pass that follows doesn't count the same check a second time.
    const consumedCheckIds = new Set();

    const place = ({ code, dateStr, amount, isExpected, label }) => {
      const bucket = (valuesByClinic[code] = valuesByClinic[code] || {});
      bucket.associateFee = bucket.associateFee || {};
      bucket.associateFee[dateStr] = (bucket.associateFee[dateStr] || 0) + amount;
      if (isExpected) {
        // Expected (not-yet-cleared) checks are shown for reference but do NOT
        // deduct from the balance — track the expected portion so the cell can
        // render muted/gray, and keep it OUT of `outflows` (the net driver).
        bucket.associateFeeExpected = bucket.associateFeeExpected || {};
        bucket.associateFeeExpected[dateStr] = (bucket.associateFeeExpected[dateStr] || 0) + amount;
      } else {
        bucket.outflows = bucket.outflows || {};
        bucket.outflows[dateStr] = (bucket.outflows[dateStr] || 0) + amount;
      }

      const detail = (breakdownsByClinic[code] = breakdownsByClinic[code] || {});
      detail.associateFee = detail.associateFee || {};
      detail.associateFee[dateStr] = detail.associateFee[dateStr] || [];
      detail.associateFee[dateStr].push({ label, amount, expected: isExpected });
    };

    // An expected amount sitting on a day that has already gone by never left
    // the bank — leaving it in the past reads as settled. Stack those on TODAY
    // instead, the same conservative treatment the uncleared half of the Checks
    // row gets. Returns null when there is nowhere to put it (today outside the
    // visible window), which is the caller's cue to drop the entry.
    const settleDate = (dateStr) => {
      if (dateStr && dateStr >= todayStr) return dateStr;
      return todayInWindow ? todayStr : null;
    };

    compRows.forEach((row) => {
      const code = row.clinic;
      if (!code) return;
      const checks = rowChecksMap.get(row.id) || [];
      const clearedCheck = checks.find((c) => c.cleared);
      const unclearedChecks = checks.filter((c) => !c.cleared);

      if (clearedCheck) {
        // Cleared: the money is gone. Real amount, real date, never moved.
        const parts = parseMonthDayFromDateString(clearedCheck.cleareddate);
        if (!parts) return;
        const amount = Number(clearedCheck.amount) || 0;
        if (!amount) return;
        const day = Math.min(parts.dayNum, daysInMonth);
        place({
          code,
          dateStr: `${month}-${String(day).padStart(2, "0")}`,
          amount,
          isExpected: false,
          label: row.practitioner_name,
        });
        return;
      }

      if (unclearedChecks.length) {
        // A real check exists but has not cleared. Prefer IT over the projected
        // draw — the check is what the bank will actually take, and its trandate
        // is a real date rather than a vendor average. Consumed so the
        // outstanding pass below skips it.
        unclearedChecks.forEach((c) => consumedCheckIds.add(String(c.id)));
        const amount = unclearedChecks.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
        if (!amount) return;
        const dateStr = settleDate(toISODate(unclearedChecks[0].trandate));
        if (!dateStr) return;
        place({ code, dateStr, amount, isExpected: true, label: row.practitioner_name });
        return;
      }

      // No check at all yet — project the draw onto the vendor's historical
      // average clear day within this month.
      const vendorInfo = expectedClearInfoByVendor[String(row.netsuite_vendor_id)];
      const day = Math.min(vendorInfo?.day ?? 15, daysInMonth);
      const amount = Number(calculateDdsDraw(row)) || 0;
      if (!amount) return;
      const dateStr = settleDate(`${month}-${String(day).padStart(2, "0")}`);
      if (!dateStr) return;
      place({ code, dateStr, amount, isExpected: true, label: row.practitioner_name });
    });

    // Outstanding checks from OTHER payroll months — cut, never cleared, and
    // invisible to the month-scoped pass above. All of them land on today.
    outstandingFeeChecks.forEach((check) => {
      if (consumedCheckIds.has(String(check.id))) return;
      const code = parseClinicFromMemo(check.memo, clinicCodeByUpper);
      if (!code) return;
      const amount = Number(check.amount ?? check.grosstotal) || 0;
      if (!amount) return;
      const dateStr = settleDate(toISODate(check.trandate));
      if (!dateStr) return;
      const who = vendorNameById[String(check.entity_id)];
      const when = toISODate(check.trandate);
      place({
        code,
        dateStr,
        amount,
        isExpected: true,
        // Named apart from the current month's lines: the reader needs to see
        // that this is an old check still outstanding, not this month's fee.
        label: `${who || check.transactionnumber || "Check"} · outstanding${when ? ` since ${when}` : ""}`,
      });
    });

    return { valuesByClinic, breakdownsByClinic };
  }, [
    compRows,
    rowChecksMap,
    expectedClearInfoByVendor,
    month,
    from,
    to,
    outstandingFeeChecks,
    clinicCodeByUpper,
    vendorNameById,
  ]);

  // ── Checks row ──────────────────────────────────────────────────────────
  // NetSuite checks cut BY THE CLINIC's own subsidiary (not by vendor), split
  // into two queries because the two halves land on different dates:
  //   cleared   → the money is already gone, so each check sits on its own
  //               `cleareddate` inside the visible window.
  //   uncleared → the date it will clear is unknown, so EVERY outstanding check
  //               (no date filter at all) is stacked on TODAY — the
  //               conservative read: assume the bank takes it now.
  const subsidiaryIdByClinic = useMemo(() => {
    const map = {};
    (allClinicsQuery.data?.results ?? []).forEach((c) => {
      if (c.clinic && c.netsuite_subsidiary_id != null) map[c.clinic] = c.netsuite_subsidiary_id;
    });
    return map;
  }, [allClinicsQuery.data]);

  // Reverse lookup for the response rows, which come back keyed by subsidiary.
  const clinicBySubsidiaryId = useMemo(() => {
    const map = {};
    sortedClinics.forEach((code) => {
      const sid = subsidiaryIdByClinic[code];
      if (sid != null) map[String(sid)] = code;
    });
    return map;
  }, [sortedClinics, subsidiaryIdByClinic]);

  // "<netsuite_subsidiary_id>:<bank account id>" for every visible clinic —
  // each clinic's subsidiary paired with its OWN active `netsuite_bank_balance`
  // accounts (the very accounts whose balance this grid projects).
  //
  // Sent as pairs rather than two flat lists because the search covers many
  // clinics at once: a flat `subsidiaries × accounts` filter would also match a
  // check cut by clinic A's subsidiary out of clinic B's account, and deduct it
  // from a balance it never touched.
  const checkPairs = useMemo(() => {
    const out = [];
    const visible = new Set(sortedClinics);
    (allClinicsQuery.data?.results ?? []).forEach((c) => {
      if (!c.clinic || !visible.has(c.clinic)) return;
      const subsidiaryId = c.netsuite_subsidiary_id;
      if (subsidiaryId == null) return;
      (c.netsuite_bank_balance ?? []).forEach((entry) => {
        if (entry && typeof entry === "object" && entry.active && entry.id != null) {
          out.push(`${subsidiaryId}:${entry.id}`);
        }
      });
    });
    return out;
  }, [allClinicsQuery.data, sortedClinics]);

  // Only `pairs` is sent — deliberately NOT `subsidiaries` as well. A
  // subsidiary named on its own matches checks from any account, so a clinic
  // with no bank account configured would quietly pull in every check its
  // subsidiary ever cut. Pairs-only means a clinic contributes exactly the
  // accounts it has configured, or nothing.
  //
  // Filtered on cleareddate, NOT trandate: the row is placed on the day the
  // money left, and a check written before the window can clear inside it.
  const clearedChecksQuery = useSearchNetsuiteChecksBySubsidiary(
    {
      pairs: checkPairs,
      dateFrom: from,
      dateTo: to,
      dateField: "cleareddate",
      cleared: "T",
      hasTranid: true,
    },
    {
      enabled: checkPairs.length > 0 && !!from && !!to,
      // Stepping the window changes the key, and a SuiteQL miss takes seconds.
      // Keep showing the previous window's checks meanwhile instead of emptying
      // the row and blurring the whole grid.
      placeholderData: (prev) => prev,
    },
  );
  // Uncleared checks are bounded by a LOOKBACK rather than the visible window:
  // one written months ago is still owed, so it can't be filtered to [from, to]
  // — but "every uncleared check ever" is unbounded. NetSuite leaves `cleared`
  // NULL on checks that were never bank-reconciled, so an unfiltered search
  // walks years of history, page by page, and is what made this grid crawl.
  const unclearedFrom = useMemo(() => monthStartBefore(CHECK_LOOKBACK_MONTHS), []);

  const unclearedChecksQuery = useSearchNetsuiteChecksBySubsidiary(
    {
      pairs: checkPairs,
      dateFrom: unclearedFrom,
      dateField: "trandate",
      cleared: "F",
      hasTranid: true,
    },
    { enabled: checkPairs.length > 0 },
  );

  // NetSuite transaction ids the reconciler already matched to a scheduled
  // payment. That payment is in its row already, so the check behind it is not
  // added a second time.
  const reconciledTransactionIds = useMemo(() => {
    const ids = new Set();
    (reconciliationQuery.data?.items ?? []).forEach((item) => {
      [item.match?.ledger?.reference, item.match?.reference].forEach((ref) => {
        const m = /^ledger:(\d+)\//.exec(ref ?? "");
        if (m) ids.add(m[1]);
      });
    });
    return ids;
  }, [reconciliationQuery.data]);

  // clinic code → { checks: {date: amt}, outflows: {date: amt} } plus the
  // per-cell breakdown lines (one line per check, titled with its memo).
  // Only numbered checks (tranid) go in Checks; the rest land in Rent / Loan /
  // Interest / Other payments by memo (see checkRowFor), and those rows are
  // returned in `extraRowsByClinic` so the card renders them.
  const checksByClinic = useMemo(() => {
    const valuesByClinic = {};
    const breakdownsByClinic = {};
    const extraRowsByClinic = {};
    if (!from || !to) return { valuesByClinic, breakdownsByClinic, extraRowsByClinic };

    const todayStr = torontoToday();
    const todayInWindow = todayStr >= from && todayStr <= to;

    // Consumed as checks are placed, so each scheduled occurrence can account
    // for at most one check. Built fresh on every run of this memo — it is
    // mutated below and must not leak into the next render.
    const schedulePool = scheduledAmountsByDate(occurrencesByClinic);

    const place = (row, dateStr, fallbackLabel, { uncleared = false } = {}) => {
      const code = clinicBySubsidiaryId[String(row.subsidiary_id)];
      if (!code || !dateStr || dateStr < from || dateStr > to) return;
      const amount = Number(row.amount ?? row.grosstotal) || 0;
      if (!amount) return;
      if (row.id != null && reconciledTransactionIds.has(String(row.id))) return;

      // Same money as a scheduled payment due that day → the schedule already
      // represents it, so the check is not drawn as well. Matched on the
      // check's OWN trandate, not `dateStr`: an uncleared check is displayed on
      // today and a cleared one on its cleared date, but what it pays is the
      // schedule due on the day it was written.
      const checkDate = toISODate(row.trandate) ?? dateStr;
      if (takeScheduleMatch(schedulePool, code, checkDate, amount)) return;

      // null = not a numbered cheque. The query already excludes these; this
      // drops any that slip through rather than filing them on a guessed row.
      const target = checkRowFor(row);
      if (!target) return;
      const { key: rowKey, label: rowLabel } = target;
      if (rowLabel) {
        const rows = (extraRowsByClinic[code] = extraRowsByClinic[code] || {});
        rows[rowKey] = rowLabel;
      }

      const bucket = (valuesByClinic[code] = valuesByClinic[code] || {});
      bucket[rowKey] = bucket[rowKey] || {};
      bucket[rowKey][dateStr] = (bucket[rowKey][dateStr] || 0) + amount;
      bucket.outflows = bucket.outflows || {};
      bucket.outflows[dateStr] = (bucket.outflows[dateStr] || 0) + amount;
      if (uncleared) {
        // Tracked apart from the total so the cell can flag that some (or all)
        // of it hasn't actually left the bank yet — see the Checks row's amber
        // treatment in ClinicCashFlowCard. `<rowKey>Uncleared` is the same
        // flag the scheduled rows use, so Rent/Loan/... cells flag alike.
        const unclearedKey = `${rowKey}Uncleared`;
        bucket[unclearedKey] = bucket[unclearedKey] || {};
        bucket[unclearedKey][dateStr] = (bucket[unclearedKey][dateStr] || 0) + amount;
      }

      // Breakdown line reads "<check #> · <memo>" — the memo says what it was
      // for, the transaction number is what you search NetSuite by. Either half
      // can be missing, so the separator only appears when both are there.
      const memo = row.memo ? String(row.memo).trim() : "";
      const tranNo = row.transactionnumber ? String(row.transactionnumber).trim() : "";
      const label = [tranNo, memo || (tranNo ? "" : fallbackLabel)].filter(Boolean).join(" · ");

      const detail = (breakdownsByClinic[code] = breakdownsByClinic[code] || {});
      detail[rowKey] = detail[rowKey] || {};
      detail[rowKey][dateStr] = detail[rowKey][dateStr] || [];
      // `link` is the NetSuite check URL the endpoint builds per row — the
      // breakdown line renders it as an anchor so a check opens in NetSuite.
      detail[rowKey][dateStr].push({
        label: label || fallbackLabel,
        amount,
        link: row.link || null,
        uncleared,
      });
    };

    const clearedRows = clearedChecksQuery.data?.items ?? [];
    clearedRows.forEach((row) => place(row, toISODate(row.cleareddate), "Check"));

    // An uncleared check has no clear date, so it lands on the day the money is
    // expected to move:
    //   trandate in the FUTURE  → that trandate cell. The check is dated ahead
    //                             (post-dated / scheduled), so that is the
    //                             earliest the bank can take it.
    //   trandate today or PAST  → today's cell. It is already payable and could
    //                             clear any day, so today is the conservative
    //                             place for it — and dropping it on its old
    //                             trandate would understate what is still owed.
    const unclearedRows = unclearedChecksQuery.data?.items ?? [];
    unclearedRows.forEach((row) => {
      const tranDate = toISODate(row.trandate);
      const isFuture = tranDate != null && tranDate > todayStr;
      if (!isFuture && !todayInWindow) return;
      place(row, isFuture ? tranDate : todayStr, "Uncleared check", { uncleared: true });
    });

    return { valuesByClinic, breakdownsByClinic, extraRowsByClinic };
  }, [
    clearedChecksQuery.data,
    unclearedChecksQuery.data,
    clinicBySubsidiaryId,
    reconciledTransactionIds,
    // The schedule pool is consumed in here, so a change to the schedules has
    // to rebuild the checks — otherwise a newly added rent would not suppress
    // its check until something else happened to invalidate this memo.
    occurrencesByClinic,
    from,
    to,
  ]);

  // Merge the real Associate Fee data (and the NetSuite Checks row) additively
  // into the schedule-based outflow buckets (there are no SchedulePayment rows
  // for associate_fee, so in practice this fully determines the row — additive
  // keeps it safe if that ever changes).
  //
  // Credit-card statements merge in the same way, and additionally contribute a
  // ROW: unlike associate fees and checks, "Credit card payment" is not in
  // ROW_DEFS, so it has to be announced to the card the way a dynamic
  // expense-type row is. Hence the second return value — `extraRowsByClinic`
  // here is the schedule rows PLUS that one, which is what every consumer
  // downstream wants.
  const { mergedValuesByClinic, extraRowsByClinic } = useMemo(() => {
    const merged = {};
    const extraRows = {};
    const codes = new Set([
      ...Object.keys(valuesByClinic),
      ...Object.keys(scheduleExtraRowsByClinic),
      ...Object.keys(associateFeeByClinic.valuesByClinic),
      ...Object.keys(checksByClinic.valuesByClinic),
      ...Object.keys(statementFlowsByClinic),
    ]);
    codes.forEach((code) => {
      const base = { ...(valuesByClinic[code] ?? {}) };
      const assoc = associateFeeByClinic.valuesByClinic[code];
      const checks = checksByClinic.valuesByClinic[code];
      if (assoc) {
        base.associateFee = { ...(base.associateFee ?? {}) };
        base.outflows = { ...(base.outflows ?? {}) };
        base.associateFeeExpected = { ...(base.associateFeeExpected ?? {}) };
        Object.entries(assoc.associateFee ?? {}).forEach(([day, amt]) => {
          base.associateFee[day] = (base.associateFee[day] || 0) + amt;
        });
        Object.entries(assoc.outflows ?? {}).forEach(([day, amt]) => {
          base.outflows[day] = (base.outflows[day] || 0) + amt;
        });
        Object.entries(assoc.associateFeeExpected ?? {}).forEach(([day, amt]) => {
          base.associateFeeExpected[day] = (base.associateFeeExpected[day] || 0) + amt;
        });
      }
      if (checks) {
        // Every key the checks produced: `checks`, the Rent/Loan/Interest/
        // Other rows unnumbered checks are filed under, their `<key>Uncleared`
        // flags and `outflows`. Added onto the scheduled amounts in the same
        // rows.
        Object.entries(checks).forEach(([field, byDate]) => {
          base[field] = { ...(base[field] ?? {}) };
          Object.entries(byDate).forEach(([day, amt]) => {
            base[field][day] = (base[field][day] || 0) + amt;
          });
        });
      }
      const statements = statementFlowsByClinic[code];
      const cardPayment = statements?.cardPayment ?? {};
      if (Object.keys(cardPayment).length) {
        base[CARD_PAYMENT_KEY] = { ...(base[CARD_PAYMENT_KEY] ?? {}) };
        base.outflows = { ...(base.outflows ?? {}) };
        Object.entries(cardPayment).forEach(([day, amt]) => {
          base[CARD_PAYMENT_KEY][day] = (base[CARD_PAYMENT_KEY][day] || 0) + amt;
          base.outflows[day] = (base.outflows[day] || 0) + amt;
        });
      }
      merged[code] = base;
      // Sorted with the schedule rows rather than appended after them, so the
      // statement row takes its place in the canonical outflow order (after the
      // named expense types, ahead of "Other payments") instead of always last.
      const scheduleRows = scheduleExtraRowsByClinic[code] ?? [];
      const checkRows = Object.entries(checksByClinic.extraRowsByClinic[code] ?? {})
        .filter(([key]) => !scheduleRows.some((r) => r.key === key))
        .map(([key, label]) => ({ key, label }));
      extraRows[code] = sortOutflowRows([
        ...scheduleRows,
        ...checkRows,
        ...(Object.keys(cardPayment).length
          ? [{ key: CARD_PAYMENT_KEY, label: CARD_PAYMENT_LABEL }]
          : []),
      ]);
    });
    return { mergedValuesByClinic: merged, extraRowsByClinic: extraRows };
  }, [
    valuesByClinic,
    scheduleExtraRowsByClinic,
    associateFeeByClinic,
    checksByClinic,
    statementFlowsByClinic,
  ]);

  // Inflows rows (DDS/HYG Revenue): actual collection for past dates, forecast
  // for today/future. Fetched from the pre-roll start so the pre-roll projection
  // (projectedSeedByClinic) has the prev-month revenue it needs; the visible grid
  // only ever reads dates inside [from, to], so the extra month is harmless there.
  const forecastsQuery = useForecastsBatch(
    sortedClinics,
    { date_from: preRollStart ?? from, date_to: to },
    { enabled: !!month && sortedClinics.length > 0 },
  );
  const collectionQuery = useReportsCollectionSummary(
    sortedClinics,
    { date_from: preRollStart ?? from, date_to: to },
    { enabled: !!month && sortedClinics.length > 0 },
  );

  const revenueByClinic = useMemo(() => {
    const map = {};
    if (!month) return map;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Keyed by ISO date so revenue lines up with the day columns, which can
    // span more than one month.
    function addToDate(code, dateStr, dds, hyg) {
      if (!dds && !hyg) return;
      const bucket = (map[code] = map[code] || {});
      bucket.dds = bucket.dds || {};
      bucket.hyg = bucket.hyg || {};
      bucket.inflows = bucket.inflows || {};
      bucket.dds[dateStr] = (bucket.dds[dateStr] ?? 0) + dds;
      bucket.hyg[dateStr] = (bucket.hyg[dateStr] ?? 0) + hyg;
      bucket.inflows[dateStr] = (bucket.inflows[dateStr] ?? 0) + dds + hyg;
    }

    const parseDay = (dateStr) => {
      const [y, m, d] = dateStr.split("-").map(Number);
      return d ? new Date(y, m - 1, d) : null;
    };

    // Actual collection (Reports) — only for days strictly before today.
    Object.entries(collectionQuery.data ?? {}).forEach(([clinicCode, items]) => {
      const code = sortedClinics.find((c) => c.toUpperCase() === clinicCode) ?? clinicCode;
      (items ?? []).forEach((item) => {
        if (!item.date) return;
        const dateStr = String(item.date).slice(0, 10);
        const d = parseDay(dateStr);
        if (!d || d >= today) return;
        addToDate(code, dateStr, Number(item.dds) || 0, Number(item.hyg) || 0);
      });
    });

    // Forecasted DDS/HYG — only for today and future days.
    Object.entries(forecastsQuery.data ?? {}).forEach(([clinicCode, items]) => {
      const code = sortedClinics.find((c) => c.toUpperCase() === clinicCode) ?? clinicCode;
      (items ?? []).forEach((item) => {
        if (!item.date) return;
        const dateStr = String(item.date).slice(0, 10);
        const d = parseDay(dateStr);
        if (!d || d < today) return;
        const dds = item.forecast_dds != null ? parseFloat(item.forecast_dds) : 0;
        const hyg = item.forecast_hyg != null ? parseFloat(item.forecast_hyg) : 0;
        addToDate(code, dateStr, dds, hyg);
      });
    });

    return map;
  }, [collectionQuery.data, forecastsQuery.data, month, sortedClinics]);

  // Intercompany transfers touching any visible clinic. Each transfer appears on
  // BOTH clinics: as a deduction (−) on the sender and an addition (+) on the
  // receiver. Rejected transfers never move cash. Fetched from the pre-roll
  // start; transfers inside the visible window drive the display rows, while
  // pre-roll-month transfers feed the projected opening seed only.
  // clinic_id matches either side of the transfer (from_clinic / to_clinic).
  // Reuses the same id map as the balance queries; gated on it being loaded so
  // an empty list can't silently widen the fetch to every clinic.
  const transfersQuery = useTransfers(
    {
      clinic_id: selectedClinicIds.join(","),
      date_from: preRollStart ?? from,
      date_to: to,
    },
    { enabled: selectedClinicIds.length > 0 && !!from && !!to },
  );
  const { transfersByClinic, preRollTransfersNetByClinic, sfcTransfers } = useMemo(() => {
    const rows = transfersQuery.data?.results ?? transfersQuery.data ?? [];
    const map = {}; // display: window transfers with net + line items
    const preRoll = {}; // pre-roll: prev-month transfer net only (no rows)
    // SFC card: transfers split into an Inflows leg (TO SFC) and an Outflows leg
    // (FROM SFC). Each holds POSITIVE per-date totals + line items; the section
    // (inflow +, outflow −) applies the sign.
    const sfcXfers = { inflow: {}, inflowItems: [], outflow: {}, outflowItems: [] };
    const ensure = (code) => (map[code] = map[code] || { net: {}, items: [] });
    const ensurePre = (code) => (preRoll[code] = preRoll[code] || {});
    const clean = (n) => (n || "").replace(/^(?:\s*Bank Balance\s*:\s*)+/i, "").trim();
    const inWindow = (d) => !!from && !!to && d >= from && d <= to;
    const inPreRoll = (d) => !!preRollStart && !!from && d >= preRollStart && d < from;
    // SFC can be identified by its clinic code "SFC" (new records) or by its
    // NetSuite bank account id 1009 (legacy records with a null clinic code).
    const isSfcSide = (code, accountId) =>
      code === "SFC" || Number(accountId) === SFC_BANK_ACCOUNT_ID;
    rows.forEach((t) => {
      if (t.status === "rejected") return;
      const amt = Number(t.amount) || 0;
      const date = String(t.date).slice(0, 10);
      const window = inWindow(date);
      const preRollDay = inPreRoll(date);
      if (!window && !preRollDay) return;
      const fromCode = t.from_clinic_code; // null for non-clinic accounts (SFC)
      const toCode = t.to_clinic_code;
      const fromLabel = clean(t.from_account_name) || t.from_clinic_name || fromCode || "account";
      const toLabel = clean(t.to_account_name) || t.to_clinic_name || toCode || "account";

      const fromIsSfc = isSfcSide(fromCode, t.netsuite_from_account_id);
      const toIsSfc = isSfcSide(toCode, t.netsuite_to_account_id);

      // Sender — money leaves this clinic (skip SFC; it's handled below).
      if (fromCode && !fromIsSfc) {
        if (window) {
          const sender = ensure(fromCode);
          sender.net[date] = (sender.net[date] || 0) - amt;
          sender.items.push({
            key: `xfer-${t.id}-out`,
            label: `To ${toLabel}`,
            status: t.status,
            byDate: { [date]: -amt },
          });
        } else {
          const pre = ensurePre(fromCode);
          pre[date] = (pre[date] || 0) - amt;
        }
      }

      // Receiver — money arrives at this clinic (skip SFC; handled below).
      if (toCode && !toIsSfc) {
        if (window) {
          const receiver = ensure(toCode);
          receiver.net[date] = (receiver.net[date] || 0) + amt;
          receiver.items.push({
            key: `xfer-${t.id}-in`,
            label: `From ${fromLabel}`,
            status: t.status,
            byDate: { [date]: amt },
          });
        } else {
          const pre = ensurePre(toCode);
          pre[date] = (pre[date] || 0) + amt;
        }
      }

      // SFC card — only window transfers (SFC has no pre-roll projection here).
      if (!window) return;

      // Transfer TO SFC (SFC is the receiver) → SFC INFLOW.
      if (toIsSfc) {
        sfcXfers.inflow[date] = (sfcXfers.inflow[date] || 0) + amt;
        sfcXfers.inflowItems.push({
          key: `xfer-${t.id}-sfc-in`,
          label: `From ${fromLabel}`,
          status: t.status,
          byDate: { [date]: amt },
        });
      }

      // Transfer FROM SFC (SFC is the sender) → SFC OUTFLOW.
      if (fromIsSfc) {
        sfcXfers.outflow[date] = (sfcXfers.outflow[date] || 0) + amt;
        sfcXfers.outflowItems.push({
          key: `xfer-${t.id}-sfc-out`,
          label: `To ${toLabel}`,
          status: t.status,
          byDate: { [date]: amt },
        });
      }
    });
    return { transfersByClinic: map, preRollTransfersNetByClinic: preRoll, sfcTransfers: sfcXfers };
  }, [transfersQuery.data, from, to, preRollStart]);

  // Scheduled outflows for the pre-roll month (rent/loan/interest/etc.), reusing
  // the already-fetched schedules. Only the per-clinic outflow totals are
  // needed here — the pre-roll never renders rows, it only rolls the balance.
  const preRollOutflowsByClinic = useMemo(() => {
    if (!preRollStart || !from) return {};
    const rows = schedulePaymentsQuery.data?.results ?? schedulePaymentsQuery.data ?? [];
    const { valuesByClinic: preValues } = buildScheduleOutflows(
      rows,
      preRollStart,
      shiftISO(from, -1),
    );
    const out = {};
    Object.entries(preValues).forEach(([code, v]) => {
      out[code] = { ...(v.outflows ?? {}) };
    });
    // A card statement paid during the pre-roll month left the bank just as a
    // rent cheque did. Omitting it would open the window on a balance that
    // still holds money the card already took, and the whole month would sit
    // that much too high.
    Object.entries(statementFlowsByClinic).forEach(([code, v]) => {
      const pre = v.preRollCardPayment ?? {};
      if (!Object.keys(pre).length) return;
      const target = (out[code] = { ...(out[code] ?? {}) });
      Object.entries(pre).forEach(([date, amt]) => {
        target[date] = (target[date] ?? 0) + amt;
      });
    });
    return out;
  }, [schedulePaymentsQuery.data, preRollStart, from, statementFlowsByClinic]);

  // Projected opening seed for each clinic: roll the last real balance (from
  // before the pre-roll month) forward through the whole pre-roll month —
  // resetting at any real entry along the way — and take the projected ending of
  // the day before the window. That value becomes the opening of the first
  // visible day, so it lines up with the prior month's ending balance instead of
  // a stale mid-month real balance. Associate fees are omitted from the pre-roll:
  // they clear mid-month and are already baked into any later real balance.
  const projectedSeedByClinic = useMemo(() => {
    const map = {};
    const codes = Object.keys(realBalanceByClinic);
    if (!preRollDays.length) {
      codes.forEach((code) => {
        map[code] = realBalanceByClinic[code]?.seed ?? null;
      });
      return map;
    }
    const lastPreRoll = preRollDays[preRollDays.length - 1].date;
    codes.forEach((code) => {
      const rb = realBalanceByClinic[code];
      const series = computeBalanceSeries({
        days: preRollDays,
        realBalanceByDay: rb?.preRollRealByDay ?? {},
        seedBalance: rb?.seed ?? null,
        inflows: revenueByClinic[code]?.inflows ?? {},
        outflows: preRollOutflowsByClinic[code] ?? {},
        transfers: preRollTransfersNetByClinic[code] ?? {},
      });
      map[code] = series.ending[lastPreRoll] ?? rb?.seed ?? null;
    });
    return map;
  }, [
    preRollDays,
    realBalanceByClinic,
    revenueByClinic,
    preRollOutflowsByClinic,
    preRollTransfersNetByClinic,
  ]);

  // Re-date and/or re-price a single occurrence (one-off exception).
  const [moveCell, setMoveCell] = useState(null);
  const createException = useCreateSchedulePaymentException();
  const updateException = useUpdateSchedulePaymentException();
  const bulkExceptions = useBulkSchedulePaymentExceptions();

  // ── editing the SERIES behind an occurrence ────────────────────────────────
  // The move modal edits one occurrence; this opens the schedule itself, so a
  // wrong recurring amount or interval can be fixed without leaving the grid
  // for the All Payments tab. Same modal, same mutations, same permissions as
  // that tab uses — only the entry point is new.
  const [editingSchedule, setEditingSchedule] = useState(null);
  const updatePayment = useUpdateSchedulePayment();
  const deletePayment = useDeleteSchedulePayment();
  // Narrower than editing on purpose: the backend refuses an area manager or
  // practitioner lead the DELETE even though they may edit.
  const canDeleteSchedule = canMoveOccurrence && canDeleteSchedulePayments(user);

  // Accounts the schedule editor can reassign a payment to. Shaped the way
  // SchedulePaymentModal's picker expects, which is also what the All Payments
  // tab builds — the two must agree, or the same schedule would offer different
  // accounts depending on where it was opened from.
  const scheduleAccountOptions = useMemo(
    () =>
      (bankAccountsQuery.data?.results ?? bankAccountsQuery.data ?? []).map((a) => ({
        value: a.id,
        label: a.label,
        clinicCode: a.clinic_code ?? null,
        entityName: a.entity_name ?? null,
        currency: a.currency,
      })),
    [bankAccountsQuery.data],
  );

  // A row in the confirm-cleared review, opened as an editable occurrence.
  //
  // That list is every payment DUE and unsettled, so the two useful answers to
  // a row that looks wrong are "the date or amount is off" and "this one never
  // went out" — an edit and a skip, neither of which is a clearance decision.
  // Ticking the row still means only what it meant before: it cleared.
  //
  // The review modal stays OPEN underneath (the editor is raised above it), so
  // fixing one payment does not throw away a half-worked set of ticks.
  function openClearedRowEditor(row) {
    if (!row?.schedule || !row?.original_date) return;
    const schedules = schedulePaymentsQuery.data?.results ?? schedulePaymentsQuery.data ?? [];
    const schedule = schedules.find((sp) => sp.id === row.schedule);
    setMoveCell({
      code: row.clinic || row.bank_account_label || "",
      rowKey: row.expense_type,
      day: null,
      occurrences: [
        {
          scheduleId: row.schedule,
          originalDate: row.original_date,
          // The EFFECTIVE date, never `display_date`: an overdue payment is
          // listed against today because it has not cleared, and prefilling
          // that would persist a move to today the moment someone saved.
          currentDate: row.date,
          amount: Number(row.amount),
          label: row.expense_type,
          exceptionId: row.exception ?? null,
          clinic: row.clinic,
          cleared: row.cleared === true,
          clearedDate: row.cleared_date ?? null,
          // Present only where a person already recorded a decision — which is
          // what stops the editor offering to skip money known to have moved.
          decision: row.decision ?? null,
          // Drives the weekday-split note. Read off the schedule rather than
          // the reconcile row, which does not carry it.
          splitOf: schedule ? splitWeekdays(schedule).length || 1 : 1,
        },
      ],
    });
  }

  function openScheduleEditor(occurrence) {
    const rows = schedulePaymentsQuery.data?.results ?? schedulePaymentsQuery.data ?? [];
    const schedule = rows.find((sp) => sp.id === occurrence?.scheduleId);
    if (!schedule) {
      showApiError(new Error("That scheduled payment could not be loaded."));
      return;
    }
    // One at a time: the move modal is editing a single occurrence of the very
    // series about to be edited, and leaving both open invites saving two
    // conflicting versions of the same payment.
    //
    // The confirm-cleared review closes too when this was reached from it. That
    // modal renders on the same layer as this one, and editing the whole series
    // is a deliberate step away from reviewing clearances rather than something
    // to do behind it.
    setMoveCell(null);
    setConfirmOpen(false);
    setEditingSchedule(schedule);
  }

  const scheduleInitialValues = useMemo(() => {
    if (!editingSchedule) return null;
    return {
      bank_account: editingSchedule.bank_account ?? "",
      expense_type: editingSchedule.expense_type,
      payment_type: editingSchedule.payment_type,
      payment_interval: editingSchedule.payment_interval,
      // Defaulted so a row predating the column reopens as "no split" rather
      // than undefined.
      payment_weekdays: editingSchedule.payment_weekdays ?? [],
      amount: editingSchedule.amount ?? "",
      start_date: editingSchedule.start_date ?? "",
      end_date: editingSchedule.end_date ?? "",
      notes: editingSchedule.notes ?? "",
      status: editingSchedule.status,
    };
  }, [editingSchedule]);

  async function handleScheduleSave(form) {
    if (!editingSchedule) return;
    try {
      await updatePayment.mutateAsync({
        id: editingSchedule.id,
        bank_account: form.bank_account || undefined,
        expense_type: form.expense_type,
        payment_type: form.payment_type,
        payment_interval: form.payment_interval,
        payment_weekdays: form.payment_weekdays ?? [],
        amount: form.amount,
        start_date: form.start_date,
        end_date: form.end_date || null,
        notes: form.notes,
        status: form.status,
      });
      showSuccess("Scheduled payment updated.");
      setEditingSchedule(null);
    } catch (err) {
      showApiError(err);
    }
  }

  async function handleScheduleDelete() {
    if (!editingSchedule) return;
    try {
      await deletePayment.mutateAsync(editingSchedule.id);
      showSuccess("Scheduled payment deleted.");
      setEditingSchedule(null);
    } catch (err) {
      showApiError(err);
    }
  }

  // ── Card statement panel editors ────────────────────────────────────────────
  //
  // The panel under each card raises an intent; the modals live here. Both are
  // the SAME editors their own tabs use — a charge is a scheduled payment (All
  // Payments), a balance and its plan are a statement (Monthly Inputs) — so a
  // record has one editor no matter which page it was opened from, and the
  // mutations, validation and error handling are not duplicated.
  const [editingStatement, setEditingStatement] = useState(null);
  const createStatement = useCreateStatementPayment();
  const updateStatement = useUpdateStatementPayment();
  // The cards' saved plans. Not bounded by month — a plan has no dates, which is
  // the point of it — and only used to pre-fill an ADD from this panel, so that
  // "add a balance" means typing one number rather than re-deriving the dates
  // and split someone already set up on Monthly Inputs.
  const statementPlansQuery = useStatementPlans(
    { status: "active", clinic: clinicFilterParam, limit: 500 },
    { enabled: sortedClinics.length > 0 },
  );
  const statementPlans = useMemo(
    () => statementPlansQuery.data?.results ?? statementPlansQuery.data ?? [],
    [statementPlansQuery.data],
  );

  // A charge line names its SERIES, so it reuses the schedule editor the grid
  // cells already open. Nothing card-specific about it — the charge just
  // happens to be drawn from a card rather than a bank account.
  function openCardChargeEditor(charge) {
    if (!charge?.scheduleId) return;
    openScheduleEditor({ scheduleId: charge.scheduleId });
  }

  // A payment line, or the panel's own action, opens the statement behind it.
  // `detail.statementId` is absent on a PENDING panel — there is no statement
  // yet — and that is exactly the case where the modal should open empty for
  // this card, which is what `null` here does.
  function openCardStatementEditor(detail) {
    const rows = statementsQuery.data?.results ?? statementsQuery.data ?? [];
    const row = detail?.statementId ? rows.find((st) => st.id === detail.statementId) : null;
    setEditingStatement({
      row,
      // Carried so an ADD opens with the card already chosen. On an edit the
      // stored row answers this, but keeping one shape for both states means
      // the initial-values memo has no second branch to get wrong.
      cardAccountId: detail?.card?.id ?? null,
    });
  }

  const statementInitialValues = useMemo(() => {
    if (!editingStatement) return null;
    const row = editingStatement.row;
    // Adding: only the card is known. Everything else is the modal's own
    // default, or filled from the card's saved plan once it is picked.
    if (!row) {
      const cardId = editingStatement.cardAccountId ?? "";
      // A card with a saved plan opens filled in from it — dates, plan and
      // split — leaving the balance to type. Same helper Monthly Inputs uses,
      // so the two entry points derive the same statement.
      const plan = statementPlans.find((pl) => String(pl.to_card_account) === String(cardId));
      return plan ? planToFormValues(plan, month) : { to_card_account: cardId };
    }
    // Editing: the stored row, mapped onto the modal's form shape. Identical to
    // Monthly Inputs' mapping — `splits` repopulates the custom rows so a custom
    // plan reopens exactly as it was saved.
    return {
      from_bank_account: row.from_bank_account ?? "",
      to_card_account: row.to_card_account ?? "",
      statement_date: row.statement_date ?? "",
      due_date: row.due_date ?? "",
      balance: row.balance ?? "",
      payment_plan: row.payment_plan ?? "full_on_due",
      plan_count: row.plan_count ?? "",
      single_date: row.payment_plan === "single_payment" ? (row.splits?.[0]?.date ?? "") : "",
      custom: (row.splits ?? []).length
        ? row.splits.map((sp) => ({ date: sp.date, amount: sp.amount }))
        : [{ date: "", amount: "" }],
      notes: row.notes ?? "",
    };
  }, [editingStatement, statementPlans, month]);

  // Raw account rows, which is the shape StatementPaymentModal's pickers want —
  // NOT the remapped `bankAccounts` above, whose `bank_name` is the account
  // label. Split by type here rather than reusing `cashAccounts`, because that
  // one is about the cash projection and drops line-of-credit accounts a
  // statement can legitimately be paid from.
  const statementAccountOptions = useMemo(() => {
    const rows = bankAccountsQuery.data?.results ?? bankAccountsQuery.data ?? [];
    return {
      bank: rows.filter((a) => a.account_type !== "credit_card"),
      card: rows.filter((a) => a.account_type === "credit_card"),
    };
  }, [bankAccountsQuery.data]);

  async function handleStatementSave(payload) {
    try {
      if (editingStatement?.row) {
        await updateStatement.mutateAsync({ id: editingStatement.row.id, ...payload });
        showSuccess("Statement updated.");
      } else {
        await createStatement.mutateAsync(payload);
        showSuccess("Statement saved.");
      }
      setEditingStatement(null);
    } catch (err) {
      showApiError(err);
    }
  }

  async function handleMove(changes) {
    if (!changes.length) {
      setMoveCell(null);
      return;
    }
    // Skips go out through the BULK endpoint, not the create/update pair the
    // moves use. Identity for a skip is (schedule, original_date) — the only
    // thing the grid reliably knows about an occurrence — and bulk upserts on
    // exactly that, so it lands whether or not an exception already exists and
    // whether or not the page's `exceptionId` is current. The create path would
    // 409 on the unique constraint in that second case.
    const skips = changes.filter((c) => c.skip);
    const edits = changes.filter((c) => !c.skip);

    try {
      await Promise.all([
        ...(skips.length
          ? [
              bulkExceptions.mutateAsync(
                skips.map(({ occurrence, reason }) => ({
                  schedule: occurrence.scheduleId,
                  // NEVER the displayed date: an overdue occurrence is drawn on
                  // today, and skipping "today" would key the exception to an
                  // occurrence the rule never produced.
                  original_date: occurrence.originalDate,
                  // The skip itself.
                  new_date: null,
                  // Omitted when blank so an upsert onto an existing exception
                  // does not wipe a note somebody already wrote there.
                  ...(reason ? { notes: reason } : {}),
                })),
              ),
            ]
          : []),
        ...edits.map(({ occurrence, newDate, newAmount }) => {
          // `amount` is sent ONLY when the user edited it, so a date-only move
          // leaves an existing override (or the schedule's own amount) alone.
          const amountPatch = newAmount != null ? { amount: newAmount } : {};
          return occurrence.exceptionId
            ? updateException.mutateAsync({
                id: occurrence.exceptionId,
                // An exception with a null new_date is a SKIP, so an amount-only
                // edit has to keep pinning the date it already sits on.
                new_date: newDate ?? occurrence.currentDate,
                ...amountPatch,
              })
            : createException.mutateAsync({
                schedule: occurrence.scheduleId,
                original_date: occurrence.originalDate,
                new_date: newDate ?? occurrence.currentDate,
                ...amountPatch,
              });
        }),
      ]);
      showSuccess(
        !edits.length
          ? "Payment skipped. Restore it from All Payments if that was a mistake."
          : "Payment updated.",
      );
      setMoveCell(null);
    } catch (err) {
      showApiError(err);
    }
  }

  // Top-of-page totals: today's opening, inflow, outflow, net, and ending
  // summed across all selected clinics. "Today" is clamped to the visible
  // window (its first date for a future window, its last date for a past one).
  const summary = useMemo(() => {
    if (!sortedClinics.length || !month || !from || !to) return {};
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const todayDate = todayStr < from ? from : todayStr > to ? to : todayStr;
    let opening = null;
    let totalInflow = 0;
    let totalOutflow = 0;
    let ending = null;
    sortedClinics.forEach((code) => {
      const clinicInflows = revenueByClinic[code]?.inflows ?? {};
      const clinicOutflows = mergedValuesByClinic[code]?.outflows ?? {};
      const clinicTransfers = transfersByClinic[code]?.net ?? {};
      totalInflow += clinicInflows[todayDate] ?? 0;
      totalOutflow += clinicOutflows[todayDate] ?? 0;
      const rb = realBalanceByClinic[code];
      if (!rb) return;
      const series = computeBalanceSeries({
        days,
        realBalanceByDay: rb.realByDay,
        seedBalance: projectedSeedByClinic[code] ?? null,
        inflows: clinicInflows,
        outflows: clinicOutflows,
        transfers: clinicTransfers,
      });
      const op = series.opening[todayDate] ?? null;
      const en = series.ending[todayDate] ?? null;
      if (op != null) opening = (opening ?? 0) + op;
      if (en != null) ending = (ending ?? 0) + en;
    });
    const netCashFlow = totalInflow - totalOutflow;
    return { opening, totalInflow, totalOutflow, netCashFlow, ending };
  }, [
    sortedClinics,
    realBalanceByClinic,
    projectedSeedByClinic,
    revenueByClinic,
    mergedValuesByClinic,
    transfersByClinic,
    days,
    month,
    from,
    to,
  ]);

  const bankAccountsForTransfer = useMemo(() => {
    // A transfer endpoint must exist in NetSuite — manually-added accounts have
    // no netsuite id and can't be a JE side. (TransferModal also guards on this.)
    const usable = bankAccounts.filter((b) => b.bank_account_id != null);
    const sfc = usable.find((b) => b.clinic === "SFC");
    const rest = usable.filter((b) => b.clinic !== "SFC");
    return sfc ? [sfc, ...rest] : rest;
  }, [bankAccounts]);

  // `cashAccounts`, not `bankAccounts`: this is the card header's subtitle —
  // the clinic's bank accounts — and a clinic-owned Visa must not claim it.
  //
  // An ARRAY per clinic, not one name: a clinic can hold several active cash
  // accounts (EVD, YOR, BEL and ART each hold two), and the header previously
  // keyed a single name on the clinic code, so whichever account the list
  // happened to yield last silently hid the others. The card lists them all.
  const bankNamesByClinic = useMemo(() => {
    const map = {};
    cashAccounts.forEach((r) => {
      if (!r.clinic || !r.bank_name) return;
      const names = (map[r.clinic] = map[r.clinic] || []);
      if (!names.includes(r.bank_name)) names.push(r.bank_name);
    });
    return map;
  }, [cashAccounts]);

  // NetSuite's own end-of-day GL balance per account, for every day the grid
  // shows — the "NetSuite Transaction Balance" row on each card. The ids are
  // the accounts' NetSuite ids (`Clinic.netsuite_bank_balance`);
  // manually-added accounts have none and simply get no row. Cash accounts
  // only: that row sits under Available Balance and describes the clinic's bank
  // account, so a card's GL history would neither belong there nor be worth the
  // extra GL sum (the slowest call on the page).
  const netsuiteAccountIds = useMemo(() => {
    const ids = new Set();
    cashAccounts.forEach((r) => {
      if (r.bank_account_id != null) ids.add(Number(r.bank_account_id));
    });
    return [...ids];
  }, [cashAccounts]);

  // One request covers every account over the whole visible window, so widening
  // the range or adding a clinic does not multiply calls.
  const netsuiteBalanceRange = useMemo(
    () => ({
      from: days.length ? days[0].date : null,
      to: days.length ? days[days.length - 1].date : null,
    }),
    [days],
  );

  const netsuiteBalancesQuery = useNetsuiteBankBalances(
    {
      ids: netsuiteAccountIds,
      from: netsuiteBalanceRange.from,
      to: netsuiteBalanceRange.to,
    },
    // Deliberately NOT part of `contentFetching` below: this is supplementary
    // reference data, and summing GL lines is the slowest call on the page. It
    // fills in when it lands instead of blurring the whole grid behind it.
    // Nothing renders until a clinic is picked, so don't pay for the page's
    // slowest call before then.
    { enabled: netsuiteAccountIds.length > 0 && sortedClinics.length > 0 },
  );

  // clinic code → the NetSuite account ids behind it. Drives whether the row
  // appears at all, independently of whether the balance request has landed.
  // A list, for the same reason `bankNamesByClinic` is: one clinic, several
  // accounts.
  const netsuiteAccountIdsByClinic = useMemo(() => {
    const map = {};
    cashAccounts.forEach((r) => {
      if (!r.clinic || r.bank_account_id == null) return;
      (map[r.clinic] = map[r.clinic] || []).push(r.bank_account_id);
    });
    return map;
  }, [cashAccounts]);

  // clinic code → { "YYYY-MM-DD": balance }. The endpoint already emits a value
  // for every day in the range, carrying quiet days forward, so this only has
  // to re-key its rows from account id onto clinic code.
  //
  // Where a clinic holds several cash accounts the per-day figures are SUMMED,
  // matching how `realBalanceByClinic` collapses that clinic's entered
  // balances. Anything else puts two rows on the same card on different
  // footings — one account's GL balance sitting under an Available Balance that
  // totals them all, which reads as a discrepancy rather than a subset. Same
  // single-currency caveat applies: every clinic-owned account is CAD today.
  const netsuiteBalanceByClinicByDay = useMemo(() => {
    const items = netsuiteBalancesQuery.data?.items ?? [];
    if (!items.length) return {};
    const byAccountId = {};
    items.forEach((a) => {
      if (a?.account == null) return;
      const series = {};
      (a.days ?? []).forEach((d) => {
        if (d?.date != null && d.balance != null) series[d.date] = Number(d.balance);
      });
      byAccountId[String(a.account)] = series;
    });
    const map = {};
    cashAccounts.forEach((r) => {
      if (!r.clinic || r.bank_account_id == null) return;
      const series = byAccountId[String(r.bank_account_id)];
      if (!series) return;
      const total = (map[r.clinic] = map[r.clinic] || {});
      Object.entries(series).forEach(([date, value]) => {
        total[date] = (total[date] ?? 0) + value;
      });
    });
    return map;
  }, [netsuiteBalancesQuery.data, cashAccounts]);

  // By Company view: a single table totalling every selected clinic. Raw line
  // items (inflows/outflows and their children) sum directly by date. The
  // Opening/Ending/Net balance series is computed PER CLINIC first (the
  // real-vs-estimate opening rule is per-clinic) and then summed — the same
  // approach the page-header totals use. An opening is treated as "real" for
  // the company only when every contributing clinic's opening that day is real.
  const companyAggregate = useMemo(() => {
    if (groupMode !== "company" || !sortedClinics.length || !days.length) return null;

    const data = {};
    const opening = {};
    const ending = {};
    const netCashflow = {};
    const openingIsReal = {};
    const realCount = {};
    const openCount = {};

    // Hover breakdowns: every aggregated cell can say WHICH clinics it is made
    // of, since that is the one thing summing them away loses. Same
    // `{ rowKey → { date → [{label, amount}] } }` shape the By Clinic cards use
    // for their per-payment tooltips — here a line is a clinic, not a payment.
    // Reference-only closing figures, summed across clinics exactly as the
    // per-clinic cards compute them. Deliberately no hover breakdown: a
    // reference cell reports the LAST day in its column rather than a sum, so a
    // by-clinic panel listing every day in a weekly column would not add up to
    // the figure shown.
    const closing = {};
    const closingLoc = {};
    const closingAvailable = {};

    const breakdowns = {};

    // Summing a clinic's payments into one line loses whether the money has
    // actually left the bank — the By Clinic panel says so per payment, but a By
    // Company line is a whole clinic-day and can hold a mix. Tally the split from
    // the SAME per-payment detail the By Clinic cards render, so the two views
    // cannot disagree about what cleared.
    //
    // Null when the row has no per-payment detail behind it (revenue, opening,
    // ending, netCashflow): those are computed figures, not a list of payments,
    // and inventing "0 cleared" for them would read as a finding.
    const reconTally = (detail, field, date) => {
      const items = detail?.[field]?.[date];
      if (!items?.length) return null;
      let clearedCount = 0;
      let clearedAmount = 0;
      let unclearedCount = 0;
      let unclearedAmount = 0;
      items.forEach((it) => {
        const amt = Number(it.amount) || 0;
        if (it.cleared) {
          clearedCount += 1;
          clearedAmount += amt;
          // `expected` (an associate fee not yet cut) and `rolledForwardFrom` (an
          // overdue payment carried onto today) are both money still owed, so
          // they count with the plain uncleared checks rather than as a state of
          // their own — the panel's job here is "settled or not".
        } else if (it.uncleared || it.rolledForwardFrom || it.expected) {
          unclearedCount += 1;
          unclearedAmount += amt;
        }
      });
      if (!clearedCount && !unclearedCount) return null;
      return { clearedCount, clearedAmount, unclearedCount, unclearedAmount };
    };

    const addBreakdown = (field, date, label, amount, detail) => {
      // A clinic contributing nothing that day is noise in the list, not
      // information — the reader wants the ones that moved the number.
      if (!amount) return;
      const byDate = (breakdowns[field] = breakdowns[field] || {});
      (byDate[date] = byDate[date] || []).push({
        label,
        amount,
        recon: reconTally(detail, field, date),
      });
    };

    sortedClinics.forEach((code) => {
      const clinicLabel = clinicOptions.find((o) => o.value === code)?.label ?? code;
      const refs = realBalanceByClinic[code];
      mergeClosingInto(closing, refs?.closingByDay);
      mergeClosingInto(closingLoc, refs?.closingLocByDay);
      mergeClosingInto(closingAvailable, refs?.closingAvailableByDay);
      const t = transfersByClinic[code];
      // The same merge the By Clinic card passes as its `breakdowns` prop — keep
      // the two in step, or a clinic line here would tally against a different
      // set of payments than the one you see after switching to By Clinic.
      const clinicDetail = mergeBreakdowns(
        occurrencesByClinic[code],
        associateFeeByClinic.breakdownsByClinic[code],
        checksByClinic.breakdownsByClinic[code],
      );
      const clinicData = {
        ...(mergedValuesByClinic[code] ?? {}),
        ...(revenueByClinic[code] ?? {}),
        transfers: t?.net ?? {},
        ...Object.fromEntries((t?.items ?? []).map((i) => [i.key, i.byDate])),
      };
      // Sum every date-keyed line-item map (inflows, outflows, dds, hyg,
      // associateFee, rent, loan, interest, transfers net + line items, …).
      Object.entries(clinicData).forEach(([field, byDate]) => {
        if (!byDate || typeof byDate !== "object") return;
        const target = (data[field] = data[field] || {});
        Object.entries(byDate).forEach(([date, amt]) => {
          target[date] = (target[date] || 0) + (amt || 0);
          addBreakdown(field, date, clinicLabel, amt || 0, clinicDetail);
        });
      });

      const rb = realBalanceByClinic[code];
      const series = computeBalanceSeries({
        days,
        realBalanceByDay: rb?.realByDay ?? {},
        seedBalance: projectedSeedByClinic[code] ?? null,
        inflows: clinicData.inflows ?? {},
        outflows: clinicData.outflows ?? {},
        transfers: clinicData.transfers,
      });

      days.forEach(({ date }) => {
        netCashflow[date] = (netCashflow[date] ?? 0) + (series.netCashflow[date] ?? 0);
        addBreakdown("netCashflow", date, clinicLabel, series.netCashflow[date] ?? 0);
        if (series.opening[date] != null) {
          opening[date] = (opening[date] ?? 0) + series.opening[date];
          openCount[date] = (openCount[date] || 0) + 1;
          if (series.openingIsReal[date]) realCount[date] = (realCount[date] || 0) + 1;
          addBreakdown("opening", date, clinicLabel, series.opening[date]);
        }
        if (series.ending[date] != null) {
          ending[date] = (ending[date] ?? 0) + series.ending[date];
          addBreakdown("ending", date, clinicLabel, series.ending[date]);
        }
      });
    });

    days.forEach(({ date }) => {
      openingIsReal[date] = openCount[date] > 0 && realCount[date] === openCount[date];
    });

    // Union of any dynamic outflow rows across clinics (keeps rows that only
    // some clinics have).
    const extraKeys = new Map();
    sortedClinics.forEach((code) => {
      (extraRowsByClinic[code] ?? []).forEach((r) => extraKeys.set(r.key, r.label));
    });
    // Sorted again: the union is built by walking clinics, so even with each
    // clinic's list ordered the merged Map would interleave them.
    const extraOutflowRows = sortOutflowRows(
      [...extraKeys].map(([key, label]) => ({ key, label })),
    );

    // Union of all clinics' transfer line items (keys are unique per transfer
    // side, so no collisions across clinics).
    const transferItems = [];
    sortedClinics.forEach((code) => {
      (transfersByClinic[code]?.items ?? []).forEach((i) =>
        transferItems.push({ key: i.key, label: i.label, status: i.status }),
      );
    });

    return {
      data,
      series: { opening, ending, netCashflow, openingIsReal },
      extraOutflowRows,
      transferItems,
      breakdowns,
      closing,
      closingLoc,
      closingAvailable,
    };
  }, [
    groupMode,
    sortedClinics,
    clinicOptions,
    days,
    mergedValuesByClinic,
    revenueByClinic,
    realBalanceByClinic,
    projectedSeedByClinic,
    extraRowsByClinic,
    transfersByClinic,
    occurrencesByClinic,
    associateFeeByClinic,
    checksByClinic,
  ]);

  // SFC card for the By Company view: shows associate fees owed (outflow,
  // identical to the All Clinics total) and intercompany transfers where SFC
  // is the sender (outflow) or receiver (inflow). No balance series — SFC's
  // daily bank balance is tracked separately on the Daily Bank Balance page.
  const sfcAggregate = useMemo(() => {
    if (groupMode !== "company" || !days.length) return null;

    const data = {};
    const addToMap = (field, byDate) => {
      if (!byDate) return;
      const target = (data[field] = data[field] || {});
      Object.entries(byDate).forEach(([date, amt]) => {
        target[date] = (target[date] || 0) + (Number(amt) || 0);
      });
    };

    // OUTFLOW — Associate fee: sum across all visible clinics. These are SFC's
    // liability. The cleared portion is the real outflow (folded into `outflows`),
    // the expected portion is muted (shown but not deducted).
    sortedClinics.forEach((code) => {
      const assoc = associateFeeByClinic.valuesByClinic[code];
      if (!assoc) return;
      addToMap("associateFee", assoc.associateFee);
      addToMap("outflows", assoc.outflows);
      addToMap("associateFeeExpected", assoc.associateFeeExpected);
    });

    // INFLOW — intercompany transfers TO SFC. Folded into the Inflows total and
    // exposed as a nested collapsible "Intercompany Transfer" group.
    addToMap("inflows", sfcTransfers.inflow);
    data.inflowsXfer = sfcTransfers.inflow;
    sfcTransfers.inflowItems.forEach((i) => {
      data[i.key] = i.byDate;
    });
    const inflowTransferItems = sfcTransfers.inflowItems.map((i) => ({
      key: i.key,
      label: i.label,
      status: i.status,
    }));

    // OUTFLOW — intercompany transfers FROM SFC. Folded into the Outflows total
    // and exposed as a nested collapsible "Intercompany Transfer" group.
    addToMap("outflows", sfcTransfers.outflow);
    data.outflowsXfer = sfcTransfers.outflow;
    sfcTransfers.outflowItems.forEach((i) => {
      data[i.key] = i.byDate;
    });
    const outflowTransferItems = sfcTransfers.outflowItems.map((i) => ({
      key: i.key,
      label: i.label,
      status: i.status,
    }));

    // Net cashflow for SFC: inflow transfers − (cleared associate fee + outflow
    // transfers). No DDS/HYG inflows for SFC.
    const series = computeBalanceSeries({
      days,
      realBalanceByDay: sfcRealBalance.realByDay,
      seedBalance: sfcRealBalance.seed,
      inflows: data.inflows ?? {},
      outflows: data.outflows ?? {},
      transfers: {},
    });

    return {
      data,
      series,
      inflowTransferItems,
      outflowTransferItems,
    };
  }, [groupMode, days, sortedClinics, associateFeeByClinic, sfcTransfers, sfcRealBalance]);

  // The clinic list + daily balances gate rendering entirely (below). These
  // secondary queries fill in the numbers — while any is still fetching we keep
  // the cards visible but blurred behind a loading overlay (see Check.jsx).
  const contentFetching =
    forecastsQuery.isFetching ||
    collectionQuery.isFetching ||
    compensationsQuery.isFetching ||
    feeChecksQuery.isFetching ||
    clearedChecksQuery.isFetching ||
    unclearedChecksQuery.isFetching ||
    schedulePaymentsQuery.isFetching ||
    transfersQuery.isFetching ||
    bankAccountsQuery.isFetching;

  const allExpanded = sortedClinics.length > 0 && sortedClinics.every((c) => cardOpen[c]);

  function toggleAll() {
    const next = !allExpanded;
    setCardOpen(Object.fromEntries(sortedClinics.map((c) => [c, next])));
  }

  function toggleCard(code) {
    setCardOpen((prev) => ({ ...prev, [code]: !prev[code] }));
  }

  // Click an expected (not-yet-cleared) associate-fee cell → fund it via a
  // transfer from the clinic's bank account → SFC corporate (1009).
  // If a funding transfer for this clinic → SFC already exists, DON'T create a
  // duplicate — open the date-move dialog for the existing one instead.
  const [fundTransfer, setFundTransfer] = useState(null);
  const [moveDateTransfer, setMoveDateTransfer] = useState(null);
  const [cellMaxDate, setCellMaxDate] = useState(undefined);
  const createTransfer = useCreateTransfer();
  const updateTransfer = useUpdateTransfer();

  async function handleExpectedFeeClick({ code, clinicName, date, amount }) {
    setCellMaxDate(date); // no funding/move date may be AFTER the AF cell date
    const fromAcct = bankAccounts.find((a) => a.clinic === code);
    const fromId = fromAcct?.bank_account_id ?? "";
    const amountCents = Math.round((amount ?? 0) * 100);

    // Look for an existing funding transfer (same from/to/amount) whose date is
    // within the 30 days BEFORE this AF cell date → move it instead of dupe.
    try {
      const url = buildUrlWithParams(
        endpoints.GET_ALL_TRANSFERS,
        { clinic: code, date_from: shiftISO(date, -30), date_to: date },
        { clean: true },
      );
      const resp = await api.get(url);
      const rows = resp.data?.results ?? resp.data ?? [];
      const existing = rows.find(
        (t) =>
          t.status !== "rejected" &&
          Number(t.netsuite_from_account_id) === Number(fromId) &&
          Number(t.netsuite_to_account_id) === SFC_BANK_ACCOUNT_ID &&
          Math.round(Number(t.amount) * 100) === amountCents,
      );
      if (existing) {
        setMoveDateTransfer(existing);
        return;
      }
    } catch (err) {
      showApiError(err);
      // fall through to the create flow
    }

    // Default the funding date to today (or the cell date if today is later),
    // never after the cell date.
    const today = torontoToday();
    setFundTransfer({
      from_id: fromId,
      to_id: SFC_BANK_ACCOUNT_ID,
      amount: String(Math.round((amount ?? 0) * 100) / 100),
      date: today <= date ? today : date,
      notes: `Associate fees funding · ${clinicName || code}→ SFC`,
    });
  }

  async function handleCreateFundTransfer(payload) {
    try {
      await createTransfer.mutateAsync({ ...payload, status: "pending" });
      showSuccess("Funding transfer created (pending).");
      setFundTransfer(null);
    } catch (err) {
      showApiError(err);
    }
  }

  async function handleMoveTransferDate(newDate) {
    try {
      await updateTransfer.mutateAsync({ id: moveDateTransfer.id, date: newDate });
      showSuccess("Transfer date updated.");
      setMoveDateTransfer(null);
    } catch (err) {
      showApiError(err);
    }
  }

  // "Download CSV": each card registers a builder for its own rows, so the file
  // carries exactly the figures the grid computes rather than a second copy of
  // that logic. Cards are written in on-screen order.
  const csvExportersRef = useRef(new Map());
  const registerCsvExport = useCallback((key, build) => {
    csvExportersRef.current.set(key, build);
    return () => csvExportersRef.current.delete(key);
  }, []);
  const companyCardCode = `${sortedClinics.length} clinic${sortedClinics.length === 1 ? "" : "s"} selected`;

  function handleDownloadCsv() {
    const order = groupMode === "company" ? ["SFC", companyCardCode] : sortedClinics;
    const rows = order.flatMap((key) => csvExportersRef.current.get(key)?.() ?? []);
    downloadCsv(`cash-flow_${from}_to_${to}_${groupMode}_${viewMode}.csv`, rows);
  }

  return (
    <>
      <CashFlowSummary
        openingBalance={summary.opening}
        totalInflow={summary.totalInflow}
        totalOutflow={summary.totalOutflow}
        netCashFlow={summary.netCashFlow}
        endingBalance={summary.ending}
      />
      <ClinicFilterBar
        rangeStart={from}
        rangeEnd={to}
        rangeDays={spanDays}
        rangeIsDefault={rangeStart === torontoToday()}
        onRangeShift={(dir) => setRangeStart((s) => shiftISO(s, dir * spanDays))}
        onRangeReset={() => setRangeStart(torontoToday())}
        // Either end of the window is pickable. `from` above IS `rangeStart`
        // (days[0].date), so writing the start back is consistent; an end-date
        // pick is converted to its start by the filter bar, since the 30/60-day
        // span already fixes the distance between the two.
        onRangeStartChange={setRangeStart}
        areaManagerFilter={areaManagerFilter}
        areaManagerOptions={areaManagerOptions}
        onAreaManagerChange={(v) => {
          setAreaManagerFilter(v);
          setClinicFilters([]);
        }}
        clinicOptions={clinicOptions}
        clinicValue={clinicFilters}
        onClinicChange={setClinicFilters}
        // Withheld until a clinic is chosen: with no selection the
        // reconciliation query is disabled, so the button would offer to
        // refresh something that is not running.
        // ORGANIZER ONLY, and hidden rather than disabled for everyone else.
        // A refresh is two paged SuiteQL scans against an API that is slow and
        // concurrency-limited for every user of the account at once, and the
        // result it recomputes is shared — so one person deciding the data is
        // stale is the right model, not forty. A disabled button would still
        // invite the question; absent, it asks nothing.
        trailing={
          sortedClinics.length > 0 && canReconcile ? (
            <CacheFreshness
              query={reconciliationQuery}
              onRefresh={() => setReconcileNonce((n) => n + 1)}
            />
          ) : null
        }
      />

      {clinicsQuery.isLoading ? (
        <Loading text="Loading clinics..." />
      ) : dailyBalancesQuery.isFetching ? (
        <Loading text="Loading balances..." />
      ) : !month ? (
        <Empty>Select a month to view cash flow.</Empty>
      ) : sortedClinics.length === 0 ? (
        <Empty>Select one or more clinics to view cash flow.</Empty>
      ) : (
        <div className="space-y-4 pb-8">
          <div className="flex items-center justify-between gap-4">
            {/* By Clinic / By Company grouping (left) */}
            <SegmentedControl
              options={[
                { value: "clinic", label: "By Clinic" },
                { value: "company", label: "By Company" },
              ]}
              value={groupMode}
              onChange={setGroupMode}
            />

            <div className="flex items-center gap-4">
              <ActualExpectedLegend />
              <ReconciliationStatus query={reconciliationQuery} />
              {/* A button, not an auto-popup. Reconciliation is cached for six
                  hours and shared across everyone, so a dialog that opened by
                  itself would re-appear on every fresh window and on every
                  colleague's first load of the same data. */}
              {pendingReview.length > 0 && (
                <button
                  type="button"
                  onClick={() => setConfirmOpen(true)}
                  title={
                    `${pendingMatchCount} matched automatically; ` +
                    `${pendingReview.length - pendingMatchCount} have no NetSuite or bank row and need a judgement call.`
                  }
                  className="flex h-9 items-center gap-1.5 rounded-lg border border-palette-emerald-border bg-palette-emerald-soft px-3 text-xs font-semibold text-palette-emerald-text transition-colors hover:bg-palette-emerald-muted"
                >
                  Review {pendingReview.length} due
                  {pendingMatchCount > 0 && ` · ${pendingMatchCount} matched`}
                </button>
              )}
              {/* Span / View-mode toggles */}
              <div className="flex items-center gap-2">
                <SegmentedControl
                  options={[
                    { value: 30, label: "30 Days" },
                    { value: 60, label: "60 Days" },
                  ]}
                  value={spanDays}
                  onChange={setSpanDays}
                />
                <div className="w-px h-6 bg-palette-gray-divider" />
                <SegmentedControl
                  options={[
                    { value: "daily", label: "Daily" },
                    { value: "weekly", label: "Weekly" },
                  ]}
                  value={viewMode}
                  onChange={setViewMode}
                />
              </div>
              {groupMode === "clinic" && (
                <button
                  onClick={toggleAll}
                  className="flex items-center gap-1.5 rounded-lg border border-palette-gray-divider bg-white px-4 py-2 text-sm font-medium text-palette-gray-heading shadow-sm hover:bg-palette-gray-subtle transition-colors"
                >
                  <Chevron open={allExpanded} />
                  {allExpanded ? "Collapse All" : "Expand All"}
                </button>
              )}
              <button
                type="button"
                onClick={handleDownloadCsv}
                disabled={contentFetching}
                className="flex items-center gap-1.5 rounded-lg bg-palette-indigo-badge px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-palette-indigo-heading transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                Download CSV
              </button>
            </div>
          </div>

          <div className="relative">
            <div
              className={`space-y-4 transition ${
                contentFetching ? "blur-sm pointer-events-none select-none" : ""
              }`}
            >
              {groupMode === "company" ? (
                <>
                  {sfcAggregate && (
                    <ClinicCashFlowCard
                      code="SFC"
                      label="Smiles First Corporation"
                      days={days}
                      open={sfcOpen}
                      onToggle={() => setSfcOpen((v) => !v)}
                      data={sfcAggregate.data}
                      precomputedSeries={sfcAggregate.series}
                      closingByDay={sfcRealBalance.closingByDay}
                      closingLocByDay={sfcRealBalance.closingLocByDay}
                      closingAvailableByDay={sfcRealBalance.closingAvailableByDay}
                      inflowTransferItems={sfcAggregate.inflowTransferItems}
                      outflowTransferItems={sfcAggregate.outflowTransferItems}
                      viewMode={viewMode}
                      hideInflowRows
                      hideChecksRow
                      onRegisterCsvExport={registerCsvExport}
                    />
                  )}
                  {companyAggregate && (
                    <ClinicCashFlowCard
                      code={companyCardCode}
                      label="All Clinics"
                      days={days}
                      open={companyOpen}
                      onToggle={() => setCompanyOpen((v) => !v)}
                      data={companyAggregate.data}
                      precomputedSeries={companyAggregate.series}
                      closingByDay={companyAggregate.closing}
                      closingLocByDay={companyAggregate.closingLoc}
                      closingAvailableByDay={companyAggregate.closingAvailable}
                      extraOutflowRows={companyAggregate.extraOutflowRows}
                      transferItems={companyAggregate.transferItems}
                      breakdowns={companyAggregate.breakdowns}
                      breakdownMode="clinics"
                      viewMode={viewMode}
                      onRegisterCsvExport={registerCsvExport}
                    />
                  )}
                </>
              ) : (
                sortedClinics.map((code) => {
                  const opt = clinicOptions.find((o) => o.value === code);
                  return (
                    <ClinicCashFlowCard
                      key={code}
                      code={code}
                      label={opt?.label ?? code}
                      bankNames={bankNamesByClinic[code] ?? []}
                      netsuiteBalanceByDay={netsuiteBalanceByClinicByDay[code] ?? {}}
                      hasNetsuiteAccount={(netsuiteAccountIdsByClinic[code]?.length ?? 0) > 0}
                      days={days}
                      open={cardOpen[code] ?? true}
                      onToggle={() => toggleCard(code)}
                      realBalanceByDay={realBalanceByClinic[code]?.realByDay ?? {}}
                      seedBalance={projectedSeedByClinic[code] ?? null}
                      closingByDay={realBalanceByClinic[code]?.closingByDay ?? {}}
                      closingLocByDay={realBalanceByClinic[code]?.closingLocByDay ?? {}}
                      closingAvailableByDay={realBalanceByClinic[code]?.closingAvailableByDay ?? {}}
                      // One expandable block per clinic-owned credit card,
                      // below Closing Ending Balance. By Company gets none:
                      // like the closing rows, these are per-account reference
                      // figures with no meaningful cross-clinic sum.
                      creditCards={(cardAccountsByClinic[code] ?? []).map((c) => {
                        // Third row of the block, under Balance and Available:
                        // what the entered statement says is owed, with its due
                        // date as a chip on the label. Absent when no statement
                        // covers the window — the block keeps its two rows
                        // rather than growing a line of dashes.
                        const stmt = statementFlowsByClinic[code]?.byCard?.[c.key];
                        return {
                          key: c.key,
                          label: c.label,
                          creditLimit: c.creditLimit,
                          balanceByDay: cardBalancesByClinic[code]?.[c.key]?.balance ?? {},
                          availableByDay: cardBalancesByClinic[code]?.[c.key]?.available ?? {},
                          statementByDay: stmt?.balanceByDay ?? {},
                          statementDue: statementDueLabel(stmt?.dueDates),
                        };
                      })}
                      cardDetails={cardDetailsByClinic[code] ?? {}}
                      // Gated on the same permission as every other edit
                      // reached from this grid; without it the panel renders
                      // its lines as plain text and offers no controls.
                      onEditCardCharge={canMoveOccurrence ? openCardChargeEditor : undefined}
                      onEditCardStatement={canMoveOccurrence ? openCardStatementEditor : undefined}
                      data={{
                        ...(mergedValuesByClinic[code] ?? {}),
                        ...(revenueByClinic[code] ?? {}),
                        transfers: transfersByClinic[code]?.net ?? {},
                        ...Object.fromEntries(
                          (transfersByClinic[code]?.items ?? []).map((i) => [i.key, i.byDate]),
                        ),
                      }}
                      transferItems={(transfersByClinic[code]?.items ?? []).map((i) => ({
                        key: i.key,
                        label: i.label,
                        status: i.status,
                      }))}
                      extraOutflowRows={extraRowsByClinic[code] ?? []}
                      occurrences={occurrencesByClinic[code] ?? {}}
                      // Scheduled occurrences already have the {label, amount}
                      // shape the breakdown tooltip wants, so they feed it
                      // directly. Unnumbered checks share the Rent/Loan/... row
                      // keys with them, so the lists are merged per day rather
                      // than overwritten. This is what lets the shared
                      // "Other payments" row say WHICH payments it contains.
                      breakdowns={{
                        ...mergeBreakdowns(
                          occurrencesByClinic[code],
                          associateFeeByClinic.breakdownsByClinic[code],
                          checksByClinic.breakdownsByClinic[code],
                        ),
                        // Which card a "Credit card payment" cell paid. One
                        // bank account can pay several cards on one day, and
                        // the row sums them.
                        [CARD_PAYMENT_KEY]: statementFlowsByClinic[code]?.breakdown ?? {},
                      }}
                      onCellClick={canMoveOccurrence ? setMoveCell : undefined}
                      // Same permission as the panel below the grid uses to open
                      // a statement, so the two entry points cannot disagree
                      // about who may edit one.
                      onCardPaymentClick={canMoveOccurrence ? openCardStatementEditor : undefined}
                      onExpectedFeeClick={canFundTransfer ? handleExpectedFeeClick : undefined}
                      viewMode={viewMode}
                      onRegisterCsvExport={registerCsvExport}
                    />
                  );
                })
              )}
            </div>
            {contentFetching && (
              <div className="absolute inset-0 z-10 flex items-start justify-center pt-10 bg-white/60 backdrop-blur-sm pointer-events-none">
                <Loading text="Loading cash flow..." size="lg" />
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmClearedModal
        isOpen={confirmOpen}
        items={pendingReview}
        isSaving={confirmClearances.isPending}
        onClose={() => setConfirmOpen(false)}
        onEditOccurrence={canMoveOccurrence ? openClearedRowEditor : undefined}
        onConfirm={async (decisions) => {
          if (!decisions.length) return;
          try {
            await confirmClearances.mutateAsync(decisions);
          } catch {
            // Reported by the mutation's onError; swallowed here so a failed
            // save leaves the modal open with the selection intact rather than
            // closing as though it had worked.
            return;
          }
          // Confirming is the end of the task, so the modal closes. There is no
          // longer a per-row action that should leave it open.
          setConfirmOpen(false);
        }}
      />

      <ConfirmNonBankingMovesModal
        isOpen={nonBankingMoves.pending.length > 0}
        moves={nonBankingMoves.pending}
        onConfirm={nonBankingMoves.confirm}
        onClose={nonBankingMoves.dismiss}
        saving={nonBankingMoves.saving}
      />

      <StatementPaymentModal
        isOpen={canMoveOccurrence && !!editingStatement}
        onClose={() => setEditingStatement(null)}
        onSave={handleStatementSave}
        saving={createStatement.isPending || updateStatement.isPending}
        bankAccounts={statementAccountOptions.bank}
        cardAccounts={statementAccountOptions.card}
        initialValues={statementInitialValues}
        isEdit={!!editingStatement?.row}
        plans={statementPlans}
        // The month on screen IS the month a statement added from here is for,
        // so a saved plan's day-of-month pair resolves against it.
        month={month}
      />

      <SchedulePaymentModal
        isOpen={canMoveOccurrence && !!editingSchedule}
        onClose={() => setEditingSchedule(null)}
        onSave={handleScheduleSave}
        saving={updatePayment.isPending}
        accountOptions={scheduleAccountOptions}
        clinicOptions={clinicOptions}
        groupBy="clinic"
        initialValues={scheduleInitialValues}
        isEdit
        onDelete={canDeleteSchedule ? handleScheduleDelete : undefined}
        deleting={deletePayment.isPending}
      />

      <MoveOccurrenceModal
        isOpen={canMoveOccurrence && !!moveCell}
        cell={moveCell}
        onClose={() => setMoveCell(null)}
        onSave={handleMove}
        saving={createException.isPending || updateException.isPending || bulkExceptions.isPending}
        onEditSchedule={canMoveOccurrence ? openScheduleEditor : undefined}
        // Raised only while the confirm-cleared review is open beneath it, so
        // stacking never depends on which dialog's portal mounted last.
        zClass={confirmOpen ? "z-[60]" : "z-50"}
      />

      <TransferModal
        isOpen={canFundTransfer && !!fundTransfer}
        onClose={() => setFundTransfer(null)}
        onSave={handleCreateFundTransfer}
        clinicOptions={bankAccountsForTransfer}
        saving={createTransfer.isPending}
        initialValues={fundTransfer}
        maxDate={cellMaxDate}
        title="Fund associate fees"
        subtitle="Transfer from the clinic account to SFC — pending approval"
        submitLabel="Create pending transfer"
      />

      <MoveTransferDateModal
        isOpen={canFundTransfer && !!moveDateTransfer}
        onClose={() => setMoveDateTransfer(null)}
        transfer={moveDateTransfer}
        onSave={handleMoveTransferDate}
        saving={updateTransfer.isPending}
        maxDate={cellMaxDate}
      />
    </>
  );
}

function Empty({ children }) {
  return (
    <div className="flex items-center justify-center py-24 text-palette-gray-placeholder text-sm">
      {children}
    </div>
  );
}

// Explains the cell styling used on DDS/HYG and outflow cells: actual amounts
// (already happened) get no background, just green/red text by direction;
// expected amounts (forecast, today/future) get a green/red background too,
// since forecasted cash flow is what this page is really for.
function ActualExpectedLegend() {
  return (
    <div className="flex items-center gap-3 text-xs font-medium text-palette-gray-secondary">
      <span className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-sm border border-palette-gray-border" />
        Actual
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-sm bg-palette-emerald-soft border border-palette-emerald-badge" />
        Expected in
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-sm bg-palette-rose-soft border border-palette-rose-badge" />
        Expected out
      </span>
      <span className="flex items-center gap-1.5">
        <span className="italic text-palette-indigo-badge">~</span>
        Estimated balance
      </span>
    </div>
  );
}
